import { deleteDoc, doc, getDoc, setDoc, collection, query, where, getDocs, addDoc, updateDoc, writeBatch, orderBy, limit } from "firebase/firestore";
import { Edit, Trash, AlarmClock, Download, Info, Lock, X, Pencil, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, RefreshCw, MapPin, FolderOpen, User, Users, Scale, Landmark, CalendarDays, Hash, Tag, Phone, FileText, MoreVertical, FileDown, History, StickyNote, Check, ListTodo, Plus, Trash2, LayoutGrid, Gavel, Bell, Receipt, Wallet, MessageSquare, Paperclip, Sparkles } from "lucide-react";
import jsPDF from "jspdf";
import "../components/crm/cases/caseDetails.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React, { useEffect, useState, useRef, useCallback } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import AllowAccess from "./AllowAccessDetails";
import ExpensesTab from "./ExpensesTab";
import CaseTimeline from "./CaseTimeline";
import { addTimelineEvent } from "../services/timelineService";
import { getStatusBucket, statusLabel, CASE_STATUSES } from "../utils/caseStatus";
import { timeAgo } from "../services/dashboardService";
import ViewCaseEventsModal from "../components/ViewCaseEventsModal";
import AddCaseEventModal from "../components/AddCaseEventModal";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { getUserLabels } from "../services/labelService";
import featureGatingService from "../services/featureGatingService";
import { orderPdfProxyUrl } from "../services/ejagritiApi";
import ConsumerTimeline from "../components/ConsumerTimeline";
import { apiRequest, HC_API_HOST } from "../services/fetchCaseApi";
import CaseDetailsRail from "../components/crm/cases/CaseDetailsRail";
import { CaseDocumentsTab, CaseInvoicesTab, CaseTasksTab, CaseTeamTab, CaseCommentsTab, CaseAttachmentsTab } from "../components/crm/cases/caseLinkedTabs";
import AISummaryModal from "../components/AISummaryModal";
import SummarizeAllModal from "../components/SummarizeAllModal";
import LatestOrderSummaryCard from "../components/LatestOrderSummaryCard";
import { isCaseShared, useDisposeSharingPrompt } from "../components/DisposeSharingModal";

// Icon for a Case Information label (Overview grid) — keyword based so the
// court-specific conditional rows get a sensible icon too.
const infoIconFor = (label) => {
  const l = (label || "").toLowerCase();
  if (l.includes("advocate")) return <Scale size={14} />;
  if (/petitioner|respondent|complainant|filed by/.test(l)) return <User size={14} />;
  if (/contact|mobile|phone|email/.test(l)) return <Phone size={14} />;
  if (/date|listed|registered|hearing/.test(l)) return <CalendarDays size={14} />;
  if (/court|bench|authority/.test(l)) return <Landmark size={14} />;
  if (/status|stage|priority/.test(l)) return <Tag size={14} />;
  if (/number|cnr|section|reference|year|no\b/.test(l)) return <Hash size={14} />;
  if (/shared|team/.test(l)) return <Users size={14} />;
  return <FileText size={14} />;
};

// Avatar helpers for the hero "Assigned Team" card (same palette as the rail).
const HERO_AVATAR_COLORS = [
  { bg: "#EEF2FF", fg: "#4F46E5" },
  { bg: "#ECFDF5", fg: "#059669" },
  { bg: "#FFF7ED", fg: "#D97706" },
  { bg: "#FDF2F8", fg: "#DB2777" },
  { bg: "#EFF6FF", fg: "#2563EB" },
];
const heroInitials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";
const heroAvatarColor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return HERO_AVATAR_COLORS[Math.abs(h) % HERO_AVATAR_COLORS.length];
};

function CaseDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const tabsScrollRef = useRef(null);
  const [tabScroll, setTabScroll] = useState({ left: false, right: false });
  const updateTabScroll = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setTabScroll({
      left: scrollLeft > 1,
      right: scrollLeft + clientWidth < scrollWidth - 1,
    });
  }, []);
  const scrollTabs = (dir) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.7), behavior: "smooth" });
  };
  useEffect(() => {
    updateTabScroll();
    const el = tabsScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateTabScroll, { passive: true });
    window.addEventListener("resize", updateTabScroll);
    return () => {
      el.removeEventListener("scroll", updateTabScroll);
      window.removeEventListener("resize", updateTabScroll);
    };
  }, [updateTabScroll, activeTab, caseData]);
  const [teamNames, setTeamNames] = useState("No teams");
  const [caseCollection, setCaseCollection] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNamesModal, setShowNamesModal] = useState(false);
  const [namesModalData, setNamesModalData] = useState({ title: "", names: [] });
  const [showTextModal, setShowTextModal] = useState(false);
  const [textModalData, setTextModalData] = useState({ title: "", text: "" });
  const [showDisposeModal, setShowDisposeModal] = useState(false);
  const [showHearingDateModal, setShowHearingDateModal] = useState(false);
  const [askKeepSharing, disposeSharingModal] = useDisposeSharingPrompt();
  const [selectedHearingDate, setSelectedHearingDate] = useState(null);
  const [disposeWithDirection, setDisposeWithDirection] = useState(true);
  const [directionDate, setDirectionDate] = useState("");
  const [directionNotes, setDirectionNotes] = useState("");
  const [directionTimeframeType, setDirectionTimeframeType] = useState("month");
  const [directionTimeframeValue, setDirectionTimeframeValue] = useState("");
  const [directionTimeframeStart, setDirectionTimeframeStart] = useState("");
  const [directionTimeframeEnd, setDirectionTimeframeEnd] = useState("");
  const [isDisposing, setIsDisposing] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [userMobile, setUserMobile] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [showExportInfoModal, setShowExportInfoModal] = useState(false);
  // Consumer Court (e-Jagriti) "History & Orders" table — show 5, then View All.
  const [showAllConsumerOrders, setShowAllConsumerOrders] = useState(false);
  const [userLabels, setUserLabels] = useState([]);
  const [caseLabels, setCaseLabels] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [lockReason, setLockReason] = useState(null);
  const [showReraViewAllModal, setShowReraViewAllModal] = useState(false);
  const [reraViewAllModalData, setReraViewAllModalData] = useState({ title: "", data: null });
  const [showCaseHistoryModal, setShowCaseHistoryModal] = useState(false);
  const [showHCOrdersModal, setShowHCOrdersModal] = useState(false);
  const [isRefreshingOrders, setIsRefreshingOrders] = useState(false);
  // AI order-summary popups (per-order ✨ button + table-level Summarize All)
  const [aiSummaryModal, setAiSummaryModal] = useState({ open: false, orderId: null, orderLink: null, pdfProxyUrl: null, pdfStorageUrl: null });
  const [summarizeAllModal, setSummarizeAllModal] = useState({ open: false, orders: [], courtType: null });
  const aiSummaryGuardRef = useRef(null);
  const [editingHearingField, setEditingHearingField] = useState(null); // "next" | "previous" | null
  const [editingHearingDate, setEditingHearingDate] = useState(null);
  const [isSavingHearingDate, setIsSavingHearingDate] = useState(false);
  const [showReminderInfoModal, setShowReminderInfoModal] = useState(false);
  const { globalUser, globalData } = useAuth();

  // Light-CRM redesign: header kebab, About expand, linked docs/tasks/invoices
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [linked, setLinked] = useState({ docs: [], tasks: [], invoices: [] });

  // District Court business detail states
  const [dcBusinessModalOpen, setDcBusinessModalOpen] = useState(false);
  const [dcBusinessDetail, setDcBusinessDetail] = useState(null);
  const [dcBusinessLoading, setDcBusinessLoading] = useState(false);
  const [dcOrderLoading, setDcOrderLoading] = useState(null);
  // Last few timeline events for the hero "Recent Activity" card — same
  // subcollection the Timeline tab reads, capped at 4.
  const [heroActivity, setHeroActivity] = useState([]);
  // Case Progress editor modal — view every step, add/reorder/complete custom
  // steps stored on the case doc as `customProgressSteps`.
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [newStepLabel, setNewStepLabel] = useState("");
  const [newStepDate, setNewStepDate] = useState(null);
  const [savingSteps, setSavingSteps] = useState(false);
  // Custom (non-native) dropdown for the add-step status picker.
  const [stepStatusOpen, setStepStatusOpen] = useState(false);
  const stepStatusRef = useRef(null);
  // Inline edit of a step in the progress modal ("start" | "end" | custom id).
  const [editingStepKey, setEditingStepKey] = useState(null);
  const [stepLabelDraft, setStepLabelDraft] = useState("");
  const [stepDateDraft, setStepDateDraft] = useState(null);

  useEffect(() => {
    if (!stepStatusOpen) return;
    const onDown = (e) => {
      if (stepStatusRef.current && !stepStatusRef.current.contains(e.target)) {
        setStepStatusOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setStepStatusOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [stepStatusOpen]);
  // "View Full Details" modal — full Case Information / parties / About.
  const [showFullDetailsModal, setShowFullDetailsModal] = useState(false);
  // Notes edit modal (hero "Notes" card).
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (!id || !caseCollection) return;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, caseCollection, id, "timeline"),
            orderBy("timestamp", "desc"),
            limit(4)
          )
        );
        setHeroActivity(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Hero activity fetch:", e);
      }
    })();
  }, [id, caseCollection]);

  const MAX_VISIBLE_ROWS = 5;
  const RERA_API_BASE = "https://jreraa.valscotech.com";
  const DC_API_BASE = "https://district-court-scraper-596718606544.asia-south1.run.app";

  const getReraSessionId = () =>
    sessionStorage.getItem("upReraSessionId") ||
    localStorage.getItem("upReraSessionId");

  const handleViewHearing = async ({ filingNo, listingDate }) => {
    try {
      let effectiveSessionId = getReraSessionId();

      // Create fresh session if not available
      if (!effectiveSessionId) {
        const capRes = await fetch(`${RERA_API_BASE}/api/rera/up/generateCaptcha`);
        const capData = await capRes.json();
        effectiveSessionId = capData.sessionId;
        sessionStorage.setItem("upReraSessionId", effectiveSessionId);
        localStorage.setItem("upReraSessionId", effectiveSessionId);
      }

      const res = await fetch(`${RERA_API_BASE}/api/rera/up/getHearingDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: effectiveSessionId,
          filingNo,
          listingDate,
        }),
      });

      const newSessionId = res.headers.get("X-UpRera-SessionId");
      if (newSessionId) {
        sessionStorage.setItem("upReraSessionId", newSessionId);
        localStorage.setItem("upReraSessionId", newSessionId);
      }

      const html = await res.text();

      if (!res.ok) {
        console.error("Hearing fetch failed:", html);
        toast.error("Unable to load hearing details");
        return;
      }

      const w = window.open("", "_blank");
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      console.error(e);
      toast.error("Error loading hearing details");
    }
  };

  // District Court: get or create a fresh session
  const getDcSessionId = async () => {
    let sid = sessionStorage.getItem("dcSessionId");
    if (sid) return sid;
    const res = await fetch(`${DC_API_BASE}/api/common/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    const data = await res.json();
    sid = data?.result?.sessionId || data?.sessionId || "";
    if (sid) sessionStorage.setItem("dcSessionId", sid);
    return sid;
  };

  // District Court: set fields before business-detail call
  const dcSetFields = async (sessionId, bp) => {
    const complexCode = caseData?.payload?.detailsPayload?.complexCode ||
      caseData?.payload?.searchPayload?.complexCode || "";
    await fetch(`${DC_API_BASE}/api/common/set-fields`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        stateCode: bp.state_code,
        distCode: bp.dist_code,
        complexCode,
      }),
    });
  };

  // District Court: fetch business detail
  const handleDcBusinessClick = async (historyEntry) => {
    if (!historyEntry.business_params) {
      toast.error("Business details not available for this entry");
      return;
    }
    setDcBusinessLoading(true);
    setDcBusinessModalOpen(true);
    setDcBusinessDetail(null);
    try {
      const sessionId = await getDcSessionId();
      const bp = historyEntry.business_params;
      const complexCode = caseData?.payload?.detailsPayload?.complexCode ||
        caseData?.payload?.searchPayload?.complexCode || "";

      // Set fields first
      await dcSetFields(sessionId, bp);

      const payload = {
        sessionId,
        cino: bp.case_number1,
        court_code: bp.court_code,
        state_code: bp.state_code,
        dist_code: bp.dist_code,
        court_complex_code: complexCode,
        nextdate1: bp.nextdate1,
        case_number1: bp.case_number1,
        disposal_flag: bp.disposal_flag,
        businessDate: bp.businessDate,
        national_court_code: bp.national_court_code,
        court_no: bp.court_no,
        search_by: bp.search_by,
        srno: bp.srno,
      };

      const response = await fetch(`${DC_API_BASE}/api/partyname/business-detail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Failed to fetch business details");
      }
      setDcBusinessDetail(data.result || data);
    } catch (err) {
      console.error("DC Business detail error:", err);
      toast.error(err.message || "Failed to fetch business details");
      setDcBusinessModalOpen(false);
    } finally {
      setDcBusinessLoading(false);
    }
  };

  // District Court: handle order PDF click
  const handleDcOrderPdfClick = async (order, index) => {
    if (!order.pdfProxy) {
      toast.error("PDF not available for this order");
      return;
    }
    setDcOrderLoading(index);
    try {
      // 1. Create a fresh session (don't reuse — encrypted params are session-bound)
      const initRes = await fetch(`${DC_API_BASE}/api/common/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const initData = await initRes.json();
      const freshSid = initData?.result?.sessionId || initData?.sessionId || "";
      if (!freshSid) throw new Error("Failed to create session for PDF");

      // 2. Set fields on fresh session
      const detailsPayload = caseData?.payload?.detailsPayload || {};
      const stateCode = detailsPayload.stateCode || "";
      const distCode = detailsPayload.distCode || "";
      const cCode = detailsPayload.complexCode || caseData?.payload?.searchPayload?.complexCode || "";
      if (stateCode && distCode) {
        await fetch(`${DC_API_BASE}/api/common/set-fields`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: freshSid, stateCode, distCode, complexCode: cCode }),
        });
      }

      // 3. Re-fetch case-detail to get fresh encrypted pdfProxy URLs
      const caseRes = await fetch(`${DC_API_BASE}/api/partyname/case-detail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...detailsPayload, sessionId: freshSid }),
      });
      const freshData = await caseRes.json();
      const result = freshData?.result || {};

      // 4. Find the matching order
      const allOrders = [...(result.interim_orders || []), ...(result.final_orders || [])];
      const freshOrder = allOrders.find(
        (o) => o.order_number === order.order_number && o.order_date === order.order_date
      );

      if (!freshOrder?.pdfProxy) {
        throw new Error("Could not get fresh PDF link for this order");
      }

      // 5. Open PDF immediately
      window.open(`${DC_API_BASE}${freshOrder.pdfProxy}`, "_blank");
    } catch (err) {
      console.error("DC Order PDF error:", err);
      toast.error(err.message || "Failed to open order PDF");
    } finally {
      setDcOrderLoading(null);
    }
  };

  // District Court: print business detail
  const handleDcPrintBusiness = () => {
    if (!dcBusinessDetail) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head><title>Business Detail</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background: #f0f0f0; }
          </style>
        </head>
        <body>
          <h2>Business Detail</h2>
          <table>
            <tr><th>Court</th><td>${dcBusinessDetail.court_of || "N/A"}</td></tr>
            <tr><th>CNR Number</th><td>${dcBusinessDetail.cnr_number || "N/A"}</td></tr>
            <tr><th>Case Number</th><td>${dcBusinessDetail.case_number || "N/A"}</td></tr>
            <tr><th>Date</th><td>${dcBusinessDetail.date || "N/A"}</td></tr>
            <tr><th>Parties</th><td>${dcBusinessDetail.parties || "N/A"}</td></tr>
            <tr><th>Business</th><td>${dcBusinessDetail.business || "N/A"}</td></tr>
            <tr><th>Next Purpose</th><td>${dcBusinessDetail.next_purpose || "N/A"}</td></tr>
            <tr><th>Next Hearing Date</th><td>${dcBusinessDetail.next_hearing_date || "N/A"}</td></tr>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const navigate = useNavigate();
  const fromPage = searchParams.get('from');
  const resolvePreviousHearingDateFromHistory = (
  nextDate,
  previousDate,
  caseHistory = []
) => {
  if (!nextDate || !previousDate) return previousDate;
  if (nextDate !== previousDate) return previousDate;
  if (!Array.isArray(caseHistory) || caseHistory.length < 2) {
    return previousDate;
  }

  const sortedHistory = [...caseHistory].sort(
    (a, b) => {
      const dateA = (a.hearingDate || a.hearing_date || "").split("/").reverse().join("-");
      const dateB = (b.hearingDate || b.hearing_date || "").split("/").reverse().join("-");
      return new Date(dateB) - new Date(dateA);
    }
  );

  return sortedHistory[1]?.hearingDate || sortedHistory[1]?.hearing_date || previousDate;
};


  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchCaseDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const pendingCaseDoc = doc(db, "pending", id);
      const pendingCaseSnapshot = await getDoc(pendingCaseDoc);
      const disposedCaseDoc = doc(db, "disposed", id);
      const disposedCaseSnapshot = await getDoc(disposedCaseDoc);
      const decidedCaseDoc = doc(db, "decided", id);
      const decidedCaseSnapshot = await getDoc(decidedCaseDoc);

      let data;
      if (pendingCaseSnapshot.exists()) {
        data = pendingCaseSnapshot.data();
        setCaseCollection("pending");
      } else if (disposedCaseSnapshot.exists()) {
        data = disposedCaseSnapshot.data();
        setCaseCollection("disposed");
      } else if (decidedCaseSnapshot.exists()) {
        data = decidedCaseSnapshot.data();
        setCaseCollection("decided");
      } else {
        setError("No such case found!");
        setLoading(false);
        return;
      }

      console.log("RAW FIRESTORE DATA:", data);
      const fixedPreviousHearingDate =
  resolvePreviousHearingDateFromHistory(
    data.nextHearingDate,
    data.previousHearingDate,
    data.caseHistory
  );

      // Check if case is locked (only for pending cases)
      if (pendingCaseSnapshot.exists() && globalUser?.uid) {
        try {
          const lockStatus = await featureGatingService.getCaseLockedStatus(
            globalUser.uid,
            { id, data: () => data },
            0 // Index doesn't matter here as we're checking a specific case
          );
          setIsLocked(lockStatus.isLocked);
          setLockReason(lockStatus.reason);
          console.log('🔒 Case lock status:', lockStatus);
        } catch (error) {
          console.error('Error checking case lock status:', error);
        }
      }

      const mappedData = {
        respondentName: data.respondentName,
        appearingFor: data.team,
        caseNumber: data.caseNumber || data.caseNo,
        filingNumber: data.filingNumber || "",
        caseType: data.caseType,
        caseYear: data.caseYear || "",
        caseNotes: data.caseNotes || data.casenotes,
        court: data.court || data.courtName,
        files: data.files,
        caseFiledDate: data.caseFiledDate,
        previousHearingDate: fixedPreviousHearingDate,
        nextHearingDate: data.nextHearingDate,
        partyContactNumber: data.partyContactNo || data.partyContactNumber,
        petitionerName: data.petitionerName,
        status: data.status,
        caseHistory: data.caseHistory || [],
        owner: data.owner || globalUser?.uid,
        team: data.team || [],
        allPetitioners: data.allPetitioners || [],
        allRespondents: data.allRespondents || [],
        labels: data.labels || [],
        labelsByUser: data.labelsByUser || {},
        petitionerAdvocate: data.petitionerAdvocate || "",
        respondentAdvocate: data.respondentAdvocate || "",
        allPetitionerAdvocates: data.allPetitionerAdvocates || [],
        allRespondentAdvocates: data.allRespondentAdvocates || [],
        rawReraData: data.rawReraData || null,
        orders: data.orders || [], // High Court / District Court orders
        ncltProceedingDetails: data.ncltProceedingDetails || [],
        ncltApplicationDetails: data.ncltApplicationDetails || [],
        ncltMainCaseDetails:
          data.ncltMainCaseDetails ||
          data.rawNcltData?.fullDetails?.parentCaseDetails ||
          [],
        rawNcltData: data.rawNcltData || null,
        payload: data.payload || null,
        courtName: data.courtName || "",
        caseTransferDetails: data.caseTransferDetails || [],
        processDetails: data.processDetails || [],
        authorityDepartment: data.authorityDepartment || "",
        authorityDepartmentOther: data.authorityDepartmentOther || "",
        properties: data.properties || "",
        area: data.area || "",
        village: data.village || "",
        stayOrderDate: data.stayOrderDate || "",
        counterAffidavitStatus: data.counterAffidavitStatus || "",
        counterAppealFiled: data.counterAppealFiled || "",
      };

      const teamMembers = [];
      if (data.team && Array.isArray(data.team)) {
        const teamNamesArray = [];
        for (const uid of data.team) {
          try {
            // Try lawyers collection first
            const lawyerDoc = doc(db, "lawyers", uid);
            const lawyerSnapshot = await getDoc(lawyerDoc);
            if (lawyerSnapshot.exists()) {
              const lawyerData = lawyerSnapshot.data();
              const name = lawyerData.name || "Unknown";
              teamMembers.push({ uid, name, email: lawyerData.email || "", _isClient: false });
              teamNamesArray.push(name);
            } else {
              // Fallback: check client collection
              const clientDoc = doc(db, "client", uid);
              const clientSnapshot = await getDoc(clientDoc);
              if (clientSnapshot.exists()) {
                const clientData = clientSnapshot.data();
                const name = clientData.name || "Unknown";
                teamMembers.push({ uid, name, email: clientData.email || "", _isClient: true });
                teamNamesArray.push(name);
              }
            }
          } catch (error) {
            console.error("Error fetching team member data:", error);
          }
        }
        setTeamNames(teamNamesArray.length > 1 ? teamNamesArray.join(", ") : "No teams");
        mappedData.teamMembers = teamMembers;
      } else {
        setTeamNames("No teams");
        mappedData.teamMembers = [];
      }

      setCaseData(mappedData);
    } catch (err) {
      console.error("Error fetching case details:", err);
      setError("Error fetching case details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseDetails();
  }, [id]);

  useEffect(() => {
    const fetchUserMobile = async () => {
      if (!globalUser?.uid) return;
      try {
        const userQuery = query(
          collection(db, "lawyers"),
          where("uid", "==", globalUser.uid)
        );
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          setUserMobile(userData.mobileWithCountryCode || "");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserMobile();
  }, [globalUser]);

  // Linked documents / tasks / invoices for tab counts + tab contents.
  useEffect(() => {
    if (!id || !globalUser?.uid || !caseData) return;
    const caseNo = caseData.caseNumber;
    const clientUser = localStorage.getItem("userType") === "client";
    (async () => {
      try {
        const filesRef = collection(db, "files");
        const [byIdSnap, byNoSnap] = await Promise.all([
          getDocs(query(filesRef, where("caseId", "==", id))).catch(() => null),
          caseNo
            ? getDocs(query(filesRef, where("caseNo", "==", caseNo))).catch(() => null)
            : Promise.resolve(null),
        ]);
        const docsById = new Map();
        [byIdSnap, byNoSnap].forEach(
          (snap) =>
            snap &&
            snap.docs.forEach((d) => docsById.set(d.id, { id: d.id, ...d.data() }))
        );
        const docs = [...docsById.values()].sort((a, b) => {
          const at = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
          const bt = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
          return bt - at;
        });

        let tasks = [];
        let invoices = [];
        if (!clientUser) {
          if (caseNo) {
            const taskSnap = await getDocs(
              query(
                collection(db, "lawyers", globalUser.uid, "tasks"),
                where("linkedCaseNo", "==", caseNo)
              )
            ).catch(() => null);
            tasks = (taskSnap?.docs || [])
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => (a.dueDate || "9999").localeCompare(b.dueDate || "9999"));
          }
          const invSnap = await getDocs(
            collection(db, "lawyers", globalUser.uid, "invoices")
          ).catch(() => null);
          invoices = (invSnap?.docs || [])
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter(
              (inv) =>
                inv.selectedCase?.id === id ||
                inv.caseId === id ||
                (caseNo && inv.selectedCase?.caseNo === caseNo)
            )
            .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
        }

        setLinked({ docs, tasks, invoices });
      } catch (e) {
        console.error("Linked case data fetch:", e);
      }
    })();
  }, [id, globalUser?.uid, caseData?.caseNumber]);

  // Fetch user labels and compute case-specific labels
  useEffect(() => {
    const fetchLabels = async () => {
      if (!globalUser?.uid || !caseData) return;
      try {
        const labels = await getUserLabels(globalUser.uid);
        setUserLabels(labels);

        // Get label IDs for this case from labelsByUser mapping (preferred) or fall back to labels array
        let caseLabelIds = [];

        if (caseData.labelsByUser?.[globalUser.uid]) {
          // Use user-specific labels from labelsByUser mapping
          caseLabelIds = caseData.labelsByUser[globalUser.uid];
        } else if (caseData.labels && Array.isArray(caseData.labels) && caseData.labels.length > 0) {
          // Fall back to the labels array (older format)
          caseLabelIds = caseData.labels;
        }

        if (caseLabelIds.length > 0) {
          const caseLabelObjects = labels.filter(label => caseLabelIds.includes(label.id));
          setCaseLabels(caseLabelObjects);
        } else {
          setCaseLabels([]);
        }
      } catch (err) {
        console.error("Error fetching labels:", err);
      }
    };

    fetchLabels();
  }, [globalUser, caseData]);

  // Helper function to delete events and cancel reminders for a case
  const cleanupEventsAndReminders = async (caseNumber, userId) => {
    if (!caseNumber || !userId) {
      console.log("Missing caseNumber or userId for cleanup");
      return;
    }

    console.log(`Cleaning up events and reminders for case: ${caseNumber}`);

    try {
      const batch = writeBatch(db);
      let eventsDeleted = 0;
      let remindersDeleted = 0;

      // 1. Delete events from lawyers/{uid}/events where "Case No" matches
      const eventsQuery = query(
        collection(db, "lawyers", userId, "events"),
        where("Case No", "==", caseNumber)
      );
      const eventsSnapshot = await getDocs(eventsQuery);

      eventsSnapshot.docs.forEach((eventDoc) => {
        batch.delete(eventDoc.ref);
        eventsDeleted++;
      });

      // 2. Delete/cancel reminders from eventReminders where caseNo matches
      const remindersQuery = query(
        collection(db, "eventReminders"),
        where("caseNo", "==", caseNumber),
        where("recipientId", "==", userId)
      );
      const remindersSnapshot = await getDocs(remindersQuery);

      remindersSnapshot.docs.forEach((reminderDoc) => {
        // Delete the reminder document (Cloud Task will fail gracefully if reminder doesn't exist)
        batch.delete(reminderDoc.ref);
        remindersDeleted++;
      });

      // Commit the batch
      if (eventsDeleted > 0 || remindersDeleted > 0) {
        await batch.commit();
        console.log(`Cleanup complete: ${eventsDeleted} events deleted, ${remindersDeleted} reminders deleted`);
      } else {
        console.log("No events or reminders found to clean up");
      }
    } catch (error) {
      console.error("Error cleaning up events and reminders:", error);
      // Don't throw - allow the main operation to continue
    }
  };

  const handleDisposeCase = async (lastHearingDate) => {
    if (caseCollection !== "pending") return;

    // Shared case: let the owner choose whether the people it was shared with
    // keep seeing it after disposal.
    let keepSharing = false;
    if (isCaseShared(caseData, globalUser?.uid)) {
      const choice = await askKeepSharing(1);
      if (choice === null) return;
      keepSharing = choice;
    }

    setIsDisposing(true);
    const pendingCaseDoc = doc(db, "pending", id);
    const disposedCaseDoc = doc(db, "disposed", id);

    const formattedDirectionDate = disposeWithDirection && directionDate
      ? directionDate.split("-").reverse().join("/")
      : "";
    const trimmedDirectionNotes = disposeWithDirection ? directionNotes.trim() : "";

    const toDMY = (s) => (s ? s.split("-").reverse().join("/") : "");
    let directionTimeframe = null;
    let directionTimeframeLabel = "";
    if (disposeWithDirection) {
      if (directionTimeframeType === "range") {
        if (directionTimeframeStart && directionTimeframeEnd) {
          const startDMY = toDMY(directionTimeframeStart);
          const endDMY = toDMY(directionTimeframeEnd);
          directionTimeframe = {
            type: "range",
            start: startDMY,
            end: endDMY,
          };
          directionTimeframeLabel = `${startDMY} - ${endDMY}`;
        }
      } else if (directionTimeframeValue && Number(directionTimeframeValue) > 0) {
        const n = Number(directionTimeframeValue);
        directionTimeframe = {
          type: directionTimeframeType,
          value: n,
        };
        directionTimeframeLabel = `${n} ${directionTimeframeType}${n !== 1 ? "s" : ""}`;
      }
    }

    try {
      const disposalChanges = {
        "Status": { old: "Pending", new: "Disposed" },
        "Last Hearing Date": { old: caseData?.nextHearingDate || "—", new: lastHearingDate || "Not set" },
      };
      if (disposeWithDirection) {
        disposalChanges["Direction Date"] = { old: "—", new: formattedDirectionDate };
        if (directionTimeframeLabel) {
          disposalChanges["Direction Timeframe"] = { old: "—", new: directionTimeframeLabel };
        }
        if (trimmedDirectionNotes) {
          disposalChanges["Direction Notes"] = { old: "—", new: trimmedDirectionNotes };
        }
      }

      const disposalDescriptionParts = [];
      if (lastHearingDate) disposalDescriptionParts.push(`last hearing date: ${lastHearingDate}`);
      if (disposeWithDirection) disposalDescriptionParts.push(`direction date: ${formattedDirectionDate}`);
      if (disposeWithDirection && directionTimeframeLabel) disposalDescriptionParts.push(`timeframe: ${directionTimeframeLabel}`);

      const pendingCaseSnapshot = await getDoc(pendingCaseDoc);
      if (pendingCaseSnapshot.exists()) {
        const caseDataToDispose = pendingCaseSnapshot.data();

        // Clean up events and reminders before disposing
        await cleanupEventsAndReminders(caseDataToDispose.caseNo, globalUser?.uid);

        // Remove shared members from the default team if they have no other pending cases
        const sharedMembers = Array.isArray(caseDataToDispose.team)
          ? caseDataToDispose.team.filter((uid) => uid !== globalUser?.uid)
          : [];
        // Keep sharing: members stay on the case, so leave the default team alone.
        for (const memberUid of keepSharing ? [] : sharedMembers) {
          // Check if this member is shared on any other pending case (excluding the current one)
          const otherPendingQuery = query(
            collection(db, 'pending'),
            where('owner', '==', globalUser.uid),
            where('team', 'array-contains', memberUid)
          );
          const otherPendingSnap = await getDocs(otherPendingQuery);
          // Filter out the current case being disposed
          const otherCases = otherPendingSnap.docs.filter((d) => d.id !== id);
          if (otherCases.length === 0) {
            await featureGatingService.removeMemberFromDefaultTeam(globalUser.uid, memberUid);
          }
        }

        // Clear team on the pending doc first to trigger removal notifications via Cloud Function
        if (!keepSharing && sharedMembers.length > 0) {
          await updateDoc(pendingCaseDoc, { team: [] });
        }

        await setDoc(disposedCaseDoc, {
          ...caseDataToDispose,
          status: "disposed",
          // Keep sharing → carry `team` over so the disposed case stays shared;
          // revoke → clear all shared access.
          team: keepSharing ? caseDataToDispose.team || [] : [],
          previousHearingDate: lastHearingDate || caseDataToDispose.nextHearingDate,
          nextHearingDate: null,
          disposedWithDirection: disposeWithDirection,
          directionDate: formattedDirectionDate || null,
          directionNotes: trimmedDirectionNotes || null,
          directionTimeframe: directionTimeframe,
          directionTimeframeLabel: directionTimeframeLabel || null,
        });

        // Migrate timeline subcollection from pending -> disposed so existing history persists
        try {
          const pendingTimelineSnap = await getDocs(collection(db, "pending", id, "timeline"));
          for (const tDoc of pendingTimelineSnap.docs) {
            await setDoc(doc(db, "disposed", id, "timeline", tDoc.id), tDoc.data());
          }
        } catch (migrationErr) {
          console.error("Error migrating timeline to disposed:", migrationErr);
        }

        // Log disposal timeline event on the disposed collection so it appears in the timeline
        await addTimelineEvent(id, "disposed", {
          type: "case_disposed",
          title: disposeWithDirection ? "Case disposed with direction" : "Case disposed",
          description: disposalDescriptionParts.length
            ? `Case disposed (${disposalDescriptionParts.join(", ")})`
            : "Case disposed",
          performedBy: globalUser?.uid || "",
          performedByName: globalUser?.name || globalUser?.displayName || "",
          changes: disposalChanges,
          metadata: {
            withDirection: disposeWithDirection,
            directionDate: formattedDirectionDate,
            directionNotes: trimmedDirectionNotes,
            directionTimeframe: directionTimeframe,
            directionTimeframeLabel: directionTimeframeLabel,
            lastHearingDate: lastHearingDate || "",
          },
        });

        await deleteDoc(pendingCaseDoc);
        setCaseData((prev) => ({
          ...prev,
          status: "disposed",
          team: keepSharing ? prev.team || [] : [],
          previousHearingDate: lastHearingDate || prev.nextHearingDate,
          nextHearingDate: null,
          disposedWithDirection: disposeWithDirection,
          directionDate: formattedDirectionDate || null,
          directionNotes: trimmedDirectionNotes || null,
          directionTimeframe: directionTimeframe,
          directionTimeframeLabel: directionTimeframeLabel || null,
        }));
        setCaseCollection("disposed");
        setShowHearingDateModal(false);
        setDisposeWithDirection(true);
        setDirectionDate("");
        setDirectionNotes("");
        setDirectionTimeframeType("month");
        setDirectionTimeframeValue("");
        setDirectionTimeframeStart("");
        setDirectionTimeframeEnd("");
        toast.success(
          keepSharing
            ? "Case disposed, still shared, and reminders cleared"
            : "Case disposed, access revoked, and reminders cleared"
        );
      } else {
        setError("Case not found in pending collection.");
      }
    } catch (err) {
      setError("Error disposing the case.");
    } finally {
      setIsDisposing(false);
    }
  };

  const handleDisposeConfirm = () => {
    setShowDisposeModal(false);
    if (!caseData?.previousHearingDate && !caseData?.nextHearingDate) {
      toast.error("Please add Next Hearing Date and Previous Hearing Date before disposing the case.");
      return;
    }
    setShowHearingDateModal(true);

    const parseDMY = (s) => {
      if (!s || typeof s !== "string") return null;
      const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!m) return null;
      const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
      d.setHours(0, 0, 0, 0);
      return d;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nhd = parseDMY(caseData?.nextHearingDate);
    if (nhd && nhd >= today && caseData?.previousHearingDate) {
      setSelectedHearingDate(caseData.previousHearingDate);
    } else {
      setSelectedHearingDate(null);
    }
  };

  const handleDeleteCase = async () => {
    if (!caseCollection) {
      setError("Case collection not determined.");
      return;
    }

    const caseDoc = doc(db, caseCollection, id);
    try {
      // Clean up events and reminders before deleting the case
      if (caseData?.caseNumber && globalUser?.uid) {
        await cleanupEventsAndReminders(caseData.caseNumber, globalUser.uid);
      }

      await deleteDoc(caseDoc);
      toast.success("Case deleted and reminders cleared");
      navigate("/portal/allcases");
    } catch (err) {
      setError("Error deleting the case.");
    }
  };

  const formatPartyWithRelation = (party) => {
    if (!party) return "";
    const name = (party.name || "").trim();
    const relation = (party.relation || "").trim();
    const relationName = (party.relationName || "").trim();
    if (!name) return "";
    if (relation && relationName) return `${name} ${relation} ${relationName}`;
    return name;
  };

  const getOtherPetitioners = () => {
    try {
      const allPetitioners = caseData?.allPetitioners || [];
      if (!Array.isArray(allPetitioners) || allPetitioners.length === 0) return "";

      const mainPetitionerName = (caseData?.petitionerName || "").trim();

      const otherFormatted = allPetitioners
        .filter(p => (p.name || "").trim() !== mainPetitionerName)
        .map(formatPartyWithRelation)
        .filter(s => s.trim() !== "");

      return otherFormatted.join(", ");
    } catch (e) {
      console.error("Error getting other petitioners:", e);
      return "";
    }
  };

  const getOtherRespondents = () => {
    try {
      const allRespondents = caseData?.allRespondents || [];
      if (!Array.isArray(allRespondents) || allRespondents.length === 0) return "";

      const mainRespondentName = (caseData?.respondentName || "").trim();

      const otherFormatted = allRespondents
        .filter(r => (r.name || "").trim() !== mainRespondentName)
        .map(formatPartyWithRelation)
        .filter(s => s.trim() !== "");

      return otherFormatted.join(", ");
    } catch (e) {
      console.error("Error getting other respondents:", e);
      return "";
    }
  };

  const getOtherPetitionerAdvocates = () => {
    try {
      const allPetitionerAdvocates = caseData?.allPetitionerAdvocates || [];
      if (!Array.isArray(allPetitionerAdvocates) || allPetitionerAdvocates.length === 0) return "";

      const mainAdvocateName = (caseData?.petitionerAdvocate || "").trim();

      const otherAdvocateNames = allPetitionerAdvocates
        .filter(a => (a.name || "").trim() !== mainAdvocateName)
        .map(a => a.name || "")
        .filter(name => name.trim() !== "");

      return otherAdvocateNames.join(", ");
    } catch (e) {
      console.error("Error getting other petitioner advocates:", e);
      return "";
    }
  };

  const getOtherRespondentAdvocates = () => {
    try {
      const allRespondentAdvocates = caseData?.allRespondentAdvocates || [];
      if (!Array.isArray(allRespondentAdvocates) || allRespondentAdvocates.length === 0) return "";

      const mainAdvocateName = (caseData?.respondentAdvocate || "").trim();

      const otherAdvocateNames = allRespondentAdvocates
        .filter(a => (a.name || "").trim() !== mainAdvocateName)
        .map(a => a.name || "")
        .filter(name => name.trim() !== "");

      return otherAdvocateNames.join(", ");
    } catch (e) {
      console.error("Error getting other respondent advocates:", e);
      return "";
    }
  };

  const showNamesPopup = (title, fullName) => {
    const isCommaSeparated = fullName.includes(",");
    const names = isCommaSeparated
      ? fullName.split(",").map(n => n.trim()).filter(n => n !== "")
      : [fullName];

    setNamesModalData({ title, names });
    setShowNamesModal(true);
  };

  const renderFieldWithInfo = (label, value) => {
    if (!value) return null;

    const truncateLength = 100;
    const shouldTruncate = value.length > truncateLength;
    const displayValue = shouldTruncate ? value.substring(0, truncateLength) + "... " : value;

    return (
      <div>
        <span className="text-gray-400 text-sm">{label}: </span>
        <span className="text-white break-words">
          {displayValue}
          {shouldTruncate && (
            <button
              onClick={() => showNamesPopup(label, value)}
              className="text-[#C99F4A] hover:text-[#D19B15] transition-colors text-xs ml-1 inline"
            >
              See more
            </button>
          )}
        </span>
      </div>
    );
  };

  const isOwner = caseData?.owner === globalUser?.uid;
  const isShared = caseData?.team?.includes(globalUser?.uid);

  // Helper function to check if this is a RERA case (UP/Delhi - sections is an array)
  const isReraCase = caseData?.rawReraData?.sections && Array.isArray(caseData.rawReraData.sections);

  // Helper function to check if this is a RERA Haryana case (sections is an object with complaint_listing_details)
  const isReraHaryanaCase = caseData?.rawReraData?.sections &&
    !Array.isArray(caseData.rawReraData.sections) &&
    typeof caseData.rawReraData.sections === "object";

  // Helper function to get RERA case history from sections
  const getReraHistory = () => {
    // Haryana RERA: build from complaint_listing_details
    if (isReraHaryanaCase) {
      const listings = caseData.rawReraData.sections.complaint_listing_details || [];
      if (listings.length === 0) return null;
      return {
        headers: ["Date", "Status", "Proceedings", "Bench"],
        rows: listings.map(l => ({
          "Date": l["Date of Hearing"] || "—",
          "Status": l.Status || "—",
          "Proceedings": l["Proceedings of the day"] || "—",
          "Bench": l.Bench || "—",
        })),
      };
    }
    if (!isReraCase) return null;
    const historySection = caseData.rawReraData.sections.find(
      s => s.title?.toLowerCase().includes("case history") && s.type === "grid"
    );
    return historySection || null;
  };

  // Helper function to get RERA order history from sections
  const getReraOrderHistory = () => {
    // Haryana RERA: build order history from complaint_listing_details (only rows with Order_action)
    if (isReraHaryanaCase) {
      const listings = caseData.rawReraData.sections.complaint_listing_details || [];
      const orderRows = listings.filter(l => l.Order_action?.url);
      if (orderRows.length === 0) return null;
      return {
        headers: ["Order Date", "View Order", "Download"],
        rows: orderRows.map(l => ({
          "Order Date": l["Date of Hearing"] || "—",
          "View Order": {
            type: "view-order",
            url: l.Order_action.url,
          },
          "Download": {
            type: "order",
            downloadLink: `https://rera-haryana-596718606544.asia-south1.run.app/api/rera/hr/downloadOrder?url=${encodeURIComponent(l.Order_action.url)}`,
          },
        })),
      };
    }
    if (!isReraCase) return null;
    const orderSection = caseData.rawReraData.sections.find(
      s => s.title?.toLowerCase().includes("order history") && s.type === "grid"
    );
    return orderSection || null;
  };

  const reraHistory = getReraHistory();
  const reraOrderHistory = getReraOrderHistory();

  // District Court case detection
  const isDistrictCourtCase = caseData?.payload?.caseexportedfrom === "DistrictCourt";

  // Supreme Court case detection
  const isSupremeCourtCase = caseData?.payload?.caseexportedfrom === "SupremeCourt";

  // Supreme Court rich tab sections (persisted under payload.sciDetails at export).
  const sciDetails = caseData?.payload?.sciDetails || {};
  const sciInterlocutory = Array.isArray(sciDetails.interlocutoryApplications) ? sciDetails.interlocutoryApplications : [];
  const sciNotices = Array.isArray(sciDetails.notices) ? sciDetails.notices : [];
  const sciEarlierCourts = Array.isArray(sciDetails.earlierCourtDetails) ? sciDetails.earlierCourtDetails : [];
  const sciOrders = Array.isArray(sciDetails.judgementOrders) && sciDetails.judgementOrders.length > 0
    ? sciDetails.judgementOrders
    : (Array.isArray(caseData?.orders) ? caseData.orders : []);
  const sciList = (v) => (Array.isArray(v) ? v : v ? [v] : []);
  // SCI order titles often repeat the document name around an embedded date,
  // e.g. "ROP - of Main Case 13-01-2026 ROP - of Main Case". Collapse the
  // duplicated phrase so the cell reads "ROP - of Main Case".
  const cleanOrderName = (raw) => {
    let s = (raw || "").toString().replace(/\s+/g, " ").trim();
    if (!s) return "Order";
    s = s.replace(/^(.+?)\s+\d{1,2}-\d{1,2}-\d{2,4}\s+\1$/i, "$1");
    s = s.replace(/^(.+?)\s+\1$/i, "$1");
    return s.trim();
  };

  // Helper function to check if this is a High Court case with orders (not RERA)
  const hasHighCourtOrders = !isReraCase && !isDistrictCourtCase && !isSupremeCourtCase && Array.isArray(caseData?.orders) && caseData.orders.length > 0;

  // NCLT case detection
  const isNcltCase = caseData?.court === "NCLT" || caseData?.courtName === "NCLT";
  const ncltProceedings = caseData?.ncltProceedingDetails || [];
  const hasNcltProceedings = isNcltCase && ncltProceedings.length > 0;
  const ncltApplicationDetails = caseData?.ncltApplicationDetails || [];
  const ncltMainCaseDetails = caseData?.ncltMainCaseDetails || [];
  // NCLT case stage (e.g. "Case Next List Date: 01-07-2026"). Both export paths
  // persist the raw full details, so read the original case_stage from there;
  // fall back to the values stored on payload for older/bulk exports.
  const ncltCaseStage = isNcltCase
    ? (caseData?.rawNcltData?.fullDetails?.currentDetails?.case_stage ||
       caseData?.payload?.caseStage ||
       caseData?.payload?.statusText ||
       "")
    : "";
  const NCLT_API_BASE_URL = "https://jncltt.valscotech.com";

  // Consumer Court (e-Jagriti) case detection — extra fields + history/orders
  // preserved under payload (see services/ejagritiExport.js).
  const isConsumerCourtCase = caseData?.payload?.caseexportedfrom === "ConsumerCourt";
  const consumerInfo = caseData?.payload || {};
  const consumerHistory = Array.isArray(caseData?.payload?.historyAndOrders)
    ? caseData.payload.historyAndOrders
    : [];
  // Prefer the saved case-history timeline (has judge/court no.); fall back to
  // the case-status history rows for cases exported before timeline was stored.
  const consumerTimeline = Array.isArray(caseData?.payload?.timeline) && caseData.payload.timeline.length
    ? caseData.payload.timeline
    : consumerHistory;

  // NGT case detection — extra fields preserved by ngtCaseMapper under payload.ngt
  const isNgtCase = caseData?.payload?.caseexportedfrom === "NGT";
  const ngtInfo = caseData?.payload?.ngt || {};
  // NGT judgement/order PDFs attached to the case (kept separate from the HC/DC
  // `orders` array so it uses its own tab + simple external-link viewer).
  const ngtOrders = Array.isArray(caseData?.ngtOrders) ? caseData.ngtOrders : [];
  const hasNgtOrders = ngtOrders.length > 0;

  // UP RERA (up-rera.in) complaint detection — extra complaint fields +
  // documentary proofs / grounds preserved under payload.upRera (see
  // UpReraComplaintDetail export).
  const isUpReraCase = caseData?.payload?.caseexportedfrom === "UpRera";
  const upReraInfo = caseData?.payload?.upRera || {};
  const upReraProofs = Array.isArray(upReraInfo.documentaryProofs)
    ? upReraInfo.documentaryProofs
    : [];
  const upReraGrounds = Array.isArray(upReraInfo.groundsForComplaint)
    ? upReraInfo.groundsForComplaint
    : [];
  const hasUpReraExtras = upReraProofs.length > 0 || upReraGrounds.length > 0;

  // Handler for viewing High Court orders
  const handleViewHighCourtOrder = async (orderLink, pdfProxyUrl, pdfStorageUrl) => {
    if (!orderLink && !pdfProxyUrl && !pdfStorageUrl) {
      toast.error("No order link available");
      return;
    }
    try {
      // Preferred: the PDF persisted to Firebase Storage at export time.
      // This never expires, so it works regardless of the eCourts session.
      if (pdfStorageUrl) {
        window.open(pdfStorageUrl, "_blank");
      } else if (pdfProxyUrl) {
        // Fallback: re-fetch via the live backend using the session baked
        // into pdfProxyUrl. Only works while that session is still alive.
        const res = await fetch(`${HC_API_HOST}${pdfProxyUrl}`);
        if (!res.ok) throw new Error("Failed to fetch order PDF");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else if (orderLink) {
        // Last resort: try to open the order link directly
        window.open(orderLink, "_blank");
      }
    } catch (err) {
      console.error("Error fetching order PDF:", err);
      toast.error("Failed to load order PDF. Please try again.");
    }
  };

  // Handler for the per-order ✨ AI summary button — opens the popup modal.
  const handleAiSummary = (order, courtType) => {
    if (aiSummaryGuardRef.current) return;
    aiSummaryGuardRef.current = true;
    setTimeout(() => { aiSummaryGuardRef.current = false; }, 1000);

    const caseNum = caseData?.caseNumber || caseData?.payload?.caseNumber || id;
    const orderDate = order.orderDate || order.order_date || order.date || order.listing_date || order["Order Date"] || "unknown";
    const stableOrderId = `${caseNum}_${courtType}_${orderDate}`.replace(/[^a-zA-Z0-9_-]/g, "_");

    setAiSummaryModal({
      open: true,
      orderId: stableOrderId,
      orderLink: order.orderLink || order.url || order.documentUrl || null,
      pdfProxyUrl: order.pdfProxyUrl || order.pdfProxy || null,
      pdfStorageUrl: order.pdfStorageUrl || null,
    });
  };

  // Quick Action: summarise every order of this case in the popup modal.
  // Builds the court-appropriate order list (same mapping the order tables use).
  const handleSummarizeAllOrders = () => {
    let orders = [];
    let courtType = null;

    if (isReraCase && reraOrderHistory?.rows?.length) {
      courtType = "rera";
      orders = reraOrderHistory.rows.map((row) => {
        let orderLink = null;
        for (const header of reraOrderHistory.headers || []) {
          if (row[header]?.type === "view-order") { orderLink = row[header].url; break; }
        }
        return {
          orderId: `${caseData.caseNumber}_${row["Date"]}`.replace(/[\/\s]/g, "_"),
          orderLabel: `Order — ${row["Date"]}`,
          orderLink
        };
      }).filter(o => o.orderLink);
    } else if (isNcltCase && ncltProceedings.filter(p => p.encPath).length > 0) {
      courtType = "nclt";
      orders = ncltProceedings.filter(p => p.encPath).map(p => ({
        orderId: `${caseData.caseNumber || id}_nclt_${p.listing_date || p.order_upload_datetime || "unknown"}`.replace(/[\/\s]/g, "_"),
        orderLabel: `${p.order_name || "Order"}${p.listing_date ? ` — ${p.listing_date}` : ""}`,
        orderLink: `${NCLT_API_BASE_URL}/order?encPath=${encodeURIComponent(p.encPath)}`
      }));
    } else if (isSupremeCourtCase && sciOrders.length > 0) {
      courtType = "sc";
      orders = sciOrders.map((o) => {
        const orderUrl = o.url || o.documentUrl || "";
        return {
          orderId: `${caseData.diaryNumber || caseData.caseNumber}_${o.date}`.replace(/[\/\s]/g, "_"),
          orderLabel: `${o.documentName || caseData.caseNumber} — ${o.date}`,
          orderLink: orderUrl
        };
      });
    } else if (isDistrictCourtCase && caseData?.orders?.length > 0) {
      courtType = "dc";
      orders = caseData.orders.map((o) => ({
        orderId: `${caseData.caseNumber}_${o.order_date}`.replace(/[\/\s]/g, "_"),
        orderLabel: `${o.order_number || caseData.caseNumber} — ${o.order_date}`,
        orderLink: o.order_link || o.url || null,
        pdfProxyUrl: o.pdfProxyUrl || o.pdfProxy || null,
        pdfStorageUrl: o.pdfStorageUrl || null
      }));
    } else if (hasHighCourtOrders) {
      courtType = "hc";
      orders = caseData.orders.map((o) => ({
        orderId: `${caseData.caseNumber}_${o.orderDate}`.replace(/[\/\s]/g, "_"),
        orderLabel: `${o.orderNumber || caseData.caseNumber} — ${o.orderDate}`,
        orderLink: o.orderLink || null,
        pdfProxyUrl: o.pdfProxyUrl || null,
        pdfStorageUrl: o.pdfStorageUrl || null
      }));
    }

    if (!orders.length) {
      toast.error("No court orders available to summarize");
      return;
    }
    setSummarizeAllModal({ open: true, orders, courtType });
  };

  // Normalise an order date to canonical YYYY-MM-DD so the same order matches
  // across sources (case-status export uses DD-MM-YYYY / DD/MM/YYYY, the Court
  // Orders form returns YYYY-MM-DD).
  const normalizeOrderDate = (s) => {
    if (!s) return "";
    const str = String(s).trim();
    let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);          // YYYY-MM-DD
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    m = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);        // DD-MM-YYYY or DD/MM/YYYY
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return str;
  };

  // Stable identity for an order across sources: normalised date + order number.
  const orderKey = (o) =>
    `${normalizeOrderDate(o?.orderDate)}|${String(o?.orderNumber ?? o?.srNo ?? "").trim()}`;

  // Build the Court Orders search payload (COcaseNumber) for a stored HC case.
  // The HC portal's Court Orders tab uses court_complex_code "0"; court_code is
  // the bench. The case-type code / registration number / year come from the
  // payload captured at export time, with fallbacks across the shapes we've
  // stored over time (detailsPayload / searchPayload / flat payload).
  const buildCourtOrdersRequest = (cd) => {
    const p = cd?.payload || {};
    const dp = p.detailsPayload || {};
    const sp = p.searchPayload || {};
    const pick = (...vals) =>
      vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "") ?? "";
    return {
      court_code: pick(dp.court_code, sp.court_code, p.courtCode, p.court_code),
      state_code: pick(dp.state_code, sp.state_code, p.stateCode, p.state_code),
      // Court Orders search is always court_complex_code "0" on the HC portal.
      court_complex_code: "0",
      caseStatusSearchType: "COcaseNumber",
      case_type_order: pick(dp.case_type, sp.case_type, dp.case_type_order, sp.case_type_order),
      case_no_order: pick(dp.case_no, sp.case_no, dp.case_number, sp.case_number, cd?.caseNo, cd?.caseNumber),
      rgyearCaseOrder: pick(dp.rgyear, sp.rgyear, dp.rgyearCaseOrder, sp.rgyearCaseOrder, cd?.caseYear),
    };
  };

  // Refresh the Orders tab by scraping the High Court "Court Orders" form
  // (autonomous CAPTCHA solve on the backend) instead of relying on the orders
  // captured from the case-status page at export time.
  const handleRefreshHighCourtOrders = async () => {
    if (isRefreshingOrders) return;
    const req = buildCourtOrdersRequest(caseData);

    const missing = ["court_code", "state_code", "case_type_order", "case_no_order", "rgyearCaseOrder"]
      .filter((k) => !req[k]);
    if (missing.length) {
      console.warn("[HC Orders refresh] missing search fields", missing, "| payload was:", caseData?.payload);
      toast.error("Can't refresh — this case is missing the court-order search details.");
      return;
    }

    setIsRefreshingOrders(true);
    const toastId = toast.loading("Fetching latest court orders…");
    try {
      const res = await apiRequest("fetch-court-orders", req);
      if (!res?.ok) {
        throw new Error(res?.error || "Court orders fetch failed");
      }
      const apiOrders = Array.isArray(res.orders) ? res.orders : [];
      // Map the Court Orders API shape -> the shape this tab renders.
      const mapped = apiOrders.map((o) => ({
        orderNumber: o.srNo || "",
        orderOn: o.details || "",
        judge: "",
        orderDate: o.orderDate || "",
        orderLink: o.pdfUrl || "",
        pdfProxyUrl: "",
        pdfStorageUrl: "",
      }));

      // Merge into the existing orders: add ones we don't already have, keep all
      // existing ones (never delete), and skip duplicates. Existing entries win
      // on a match (they carry the persisted, non-expiring pdfStorageUrl).
      const existing = Array.isArray(caseData?.orders) ? caseData.orders : [];
      const seen = new Set(existing.map(orderKey));
      const additions = mapped.filter((o) => !seen.has(orderKey(o)));
      const merged = [...existing, ...additions];

      // Update the on-screen list immediately.
      setCaseData((prev) => ({ ...prev, orders: merged }));

      if (additions.length === 0) {
        toast.success("Already up to date — no new orders found", { id: toastId });
        return;
      }

      // Persist the merged list to Firestore so the additions survive a reload.
      try {
        if (caseCollection && id) {
          await updateDoc(doc(db, caseCollection, id), { orders: merged });
        }
        toast.success(
          `Added ${additions.length} new order${additions.length !== 1 ? "s" : ""}`,
          { id: toastId }
        );
      } catch (persistErr) {
        console.error("[HC Orders refresh] Firestore persist failed:", persistErr);
        toast.error(
          `Found ${additions.length} new order${additions.length !== 1 ? "s" : ""}, but couldn't save them. Please try again.`,
          { id: toastId }
        );
      }
    } catch (err) {
      console.error("[HC Orders refresh] error:", err);
      const data = err?.response?.data;
      const msg =
        (data && (typeof data.error === "string" ? data.error : data.error?.message)) ||
        err?.message ||
        "Failed to refresh court orders";
      toast.error(typeof msg === "string" ? msg : "Failed to refresh court orders", { id: toastId });
    } finally {
      setIsRefreshingOrders(false);
    }
  };

  // Format date strings from DC API (e.g. "2nd April 2026", "13-March-2024", "02-04-2026") to DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const monthNames = {
      january: "01", february: "02", march: "03", april: "04",
      may: "05", june: "06", july: "07", august: "08",
      september: "09", october: "10", november: "11", december: "12",
    };
    // "dd-mm-yyyy"
    const hyphenMatch = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (hyphenMatch) {
      return `${hyphenMatch[1]}/${hyphenMatch[2]}/${hyphenMatch[3]}`;
    }
    // "dd-Month-yyyy"
    const dashedMatch = dateStr.match(
      /^(\d{1,2})-(January|February|March|April|May|June|July|August|September|October|November|December)-(\d{4})$/i
    );
    if (dashedMatch) {
      const day = dashedMatch[1].padStart(2, "0");
      const month = monthNames[dashedMatch[2].toLowerCase()];
      return `${day}/${month}/${dashedMatch[3]}`;
    }
    // "2nd April 2026", "13th March 2024", "25th January 2024"
    const naturalMatch = dateStr.match(
      /^(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/i
    );
    if (naturalMatch) {
      const day = naturalMatch[1].padStart(2, "0");
      const month = monthNames[naturalMatch[2].toLowerCase()];
      return `${day}/${month}/${naturalMatch[3]}`;
    }
    return dateStr;
  };

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleExportCase = async () => {
    setIsExporting(true);
    try {
      // Check for duplicates
      const pendingQuery = query(
        collection(db, "pending"),
        where("owner", "==", globalUser.uid),
        where("caseNo", "==", caseData.caseNumber)
      );
      const disposedQuery = query(
        collection(db, "disposed"),
        where("owner", "==", globalUser.uid),
        where("caseNo", "==", caseData.caseNumber)
      );
      const [pendingSnapshot, disposedSnapshot] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(disposedQuery),
      ]);

      if (!pendingSnapshot.empty || !disposedSnapshot.empty) {
        toast.error("This case is already saved in your cases.");
        setIsExporting(false);
        return;
      }

      // Prepare case data to export
      const caseDataToExport = {
        caseNo: caseData.caseNumber,
        caseType: caseData.caseType,
        caseYear: caseData.caseYear,
        caseFiledDate: caseData.caseFiledDate,
        nextHearingDate: caseData.nextHearingDate,
        previousHearingDate: caseData.previousHearingDate,
        courtName: caseData.court,
        partyContactNo: caseData.partyContactNumber,
        respondentName: caseData.respondentName,
        petitionerName: caseData.petitionerName,
        files: caseData.files || [],
        caseNotes: caseData.caseNotes || "",
        status: caseData.status,
        caseHistory: caseData.caseHistory || [],
        allPetitioners: caseData.allPetitioners || [],
        allRespondents: caseData.allRespondents || [],
        labels: caseData.labels || [],
        petitionerAdvocate: caseData.petitionerAdvocate || "",
        respondentAdvocate: caseData.respondentAdvocate || "",
        allPetitionerAdvocates: caseData.allPetitionerAdvocates || [],
        allRespondentAdvocates: caseData.allRespondentAdvocates || [],
        owner: globalUser.uid,
        team: [globalUser.uid],
      };

      // Create new case
      const collectionName = getStatusBucket(caseData.status);
      const casesCollectionRef = collection(db, collectionName);
      const newDocRef = await addDoc(casesCollectionRef, caseDataToExport);

      // Create events if nextHearingDate exists
      if (caseData.nextHearingDate) {
        try {
          const eventDate = new Date(
            caseData.nextHearingDate.split("/").reverse().join("-")
          );

          // Event 1: 8:00 AM IST on the next hearing date (visible in calendar)
          const reminderDate = new Date(eventDate);
          reminderDate.setHours(8, 0, 0, 0);

          // Event 2: 6:00 PM on the next hearing date
          const eventTimeIST = new Date(eventDate);
          eventTimeIST.setHours(18, 0, 0, 0);

          // Create Event 1: Reminder at 8:00 AM one day before
          if (reminderDate > new Date()) {
            const reminderEventData = {
              "Case No": caseData.caseNumber,
              date: formatDateToYYYYMMDD(reminderDate),
              mobile: userMobile || null,
              time: "08:00 AM",
              time1: reminderDate.getTime(),
              title: `${caseData.petitionerName} VS ${caseData.respondentName}`,
              description: `Hearing Reminder : Case No. - ${caseData.caseNumber}`,
              isScheduled: reminderDate > new Date(),
              status: caseData.status,
              eventType: "reminder",
            };
            const reminderEventRef = await addDoc(
              collection(db, "lawyers", globalUser.uid, "events"),
              reminderEventData
            );

            // Add to eventReminders collection
            const reminderDoc = {
              eventId: reminderEventRef.id,
              reminderTime: reminderDate,
              eventTitle: `Hearing Reminder : Case No. - ${caseData.caseNumber}`,
              caseNo: caseData.caseNumber,
              caseId: newDocRef.id,
              collection: collectionName,
              status: "pending",
              createdAt: new Date(),
              scheduledBy: globalUser.uid,
              recipientId: globalUser.uid,
            };
            await addDoc(collection(db, "eventReminders"), reminderDoc);
          }

          // Create Event 2: Main event at 6:00 PM on next hearing date
          if (eventTimeIST > new Date()) {
            const mainEventData = {
              "Case No": caseData.caseNumber,
              date: formatDateToYYYYMMDD(eventDate),
              mobile: userMobile || null,
              time: "06:00 PM",
              time1: eventTimeIST.getTime(),
              title: `Update Next Hearing: ${caseData.petitionerName} VS ${caseData.respondentName}`,
              description: `Case No. - ${caseData.caseNumber}`,
              isScheduled: eventTimeIST > new Date(),
              status: caseData.status,
              eventType: "updateReminder",
              isHidden: true,
            };
            const mainEventRef = await addDoc(
              collection(db, "lawyers", globalUser.uid, "events"),
              mainEventData
            );

            // Add to eventReminders collection
            const mainReminderDoc = {
              eventId: mainEventRef.id,
              reminderTime: eventTimeIST,
              eventTitle: `Update Next Hearing : ${caseData.caseNumber}`,
              caseNo: caseData.caseNumber,
              caseId: newDocRef.id,
              collection: collectionName,
              status: "pending",
              createdAt: new Date(),
              scheduledBy: globalUser.uid,
              recipientId: globalUser.uid,
            };
            await addDoc(collection(db, "eventReminders"), mainReminderDoc);
          }
        } catch (error) {
          console.error("Error creating event:", error);
        }
      }

      // Remove the user from the original case's team
      const originalCaseDocRef = doc(db, caseCollection, id);
      const originalCaseSnapshot = await getDoc(originalCaseDocRef);
      if (originalCaseSnapshot.exists()) {
        const originalData = originalCaseSnapshot.data();
        const updatedTeam = originalData.team.filter(uid => uid !== globalUser.uid);
        await updateDoc(originalCaseDocRef, { team: updatedTeam });
      }

      toast.success("Case exported successfully.");
      navigate("/portal/Dashboard");
    } catch (error) {
      console.error("Error exporting case:", error);
      toast.error("Failed to export case");
    } finally {
      setIsExporting(false);
    }
  };

  // ---------- Inline hearing-date edit helpers ----------
  const parseDMYToDate = (str) => {
    if (!str) return null;
    const sep = str.includes("/") ? "/" : "-";
    const [d, m, y] = str.split(sep);
    if (!d || !m || !y) return null;
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  };

  const formatDateToDMY = (date) => {
    if (!date) return "";
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  const formatDateToYMD = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}-${d}`;
  };

  const openHearingEdit = (field) => {
    const current =
      field === "next" ? caseData?.nextHearingDate : caseData?.previousHearingDate;
    setEditingHearingField(field);
    setEditingHearingDate(parseDMYToDate(current));
  };

  const closeHearingEdit = () => {
    setEditingHearingField(null);
    setEditingHearingDate(null);
  };

  // Recreate events + eventReminders for a case's next hearing date
  // Mirrors EditCaseDetails.updateEventsByCaseNumber
  const recreateEventsForNextHearing = async (newNextHearingDmy) => {
    if (!globalUser?.uid) return;
    const caseNo = caseData?.caseNumber;
    if (!caseNo) return;

    try {
      // Delete old events for this case
      const oldEventsSnap = await getDocs(
        query(
          collection(db, "lawyers", globalUser.uid, "events"),
          where("Case No", "==", caseNo)
        )
      );
      for (const d of oldEventsSnap.docs) {
        await deleteDoc(d.ref);
      }

      // Delete old event reminders
      const oldRemindersSnap = await getDocs(
        query(
          collection(db, "eventReminders"),
          where("caseNo", "==", caseNo),
          where("recipientId", "==", globalUser.uid)
        )
      );
      for (const d of oldRemindersSnap.docs) {
        await deleteDoc(d.ref);
      }

      if (!newNextHearingDmy) return;

      const [dd, mm, yy] = newNextHearingDmy.split("/");
      const eventDate = new Date(parseInt(yy), parseInt(mm) - 1, parseInt(dd));
      const collectionName = caseCollection || "pending";

      // Event 1: 8:00 AM on hearing date (visible)
      const reminderDate = new Date(eventDate);
      reminderDate.setHours(8, 0, 0, 0);
      if (reminderDate > new Date()) {
        const reminderEventRef = await addDoc(
          collection(db, "lawyers", globalUser.uid, "events"),
          {
            "Case No": caseNo,
            date: formatDateToYMD(reminderDate),
            mobile: userMobile || null,
            time: "08:00 AM",
            time1: reminderDate.getTime(),
            title: `${caseData?.petitionerName || ""} VS ${caseData?.respondentName || ""}`,
            description: `Hearing Reminder : Case No. - ${caseNo}`,
            isScheduled: true,
            status: caseData?.status || "",
            eventType: "reminder",
          }
        );
        await addDoc(collection(db, "eventReminders"), {
          eventId: reminderEventRef.id,
          reminderTime: reminderDate,
          eventTitle: `Hearing Reminder : Case No. - ${caseNo}`,
          caseNo,
          caseId: id,
          collection: collectionName,
          status: "pending",
          createdAt: new Date(),
          scheduledBy: globalUser.uid,
          recipientId: globalUser.uid,
        });
      }

      // Event 2: 6:00 PM on hearing date (hidden update reminder)
      const eventTimeIST = new Date(eventDate);
      eventTimeIST.setHours(18, 0, 0, 0);
      if (eventTimeIST > new Date()) {
        const mainEventRef = await addDoc(
          collection(db, "lawyers", globalUser.uid, "events"),
          {
            "Case No": caseNo,
            date: formatDateToYMD(eventDate),
            mobile: userMobile || null,
            time: "06:00 PM",
            time1: eventTimeIST.getTime(),
            title: `Update Next Hearing: ${caseData?.petitionerName || ""} VS ${caseData?.respondentName || ""}`,
            description: `Case No. - ${caseNo}`,
            isScheduled: true,
            status: caseData?.status || "",
            eventType: "updateReminder",
            isHidden: true,
          }
        );
        await addDoc(collection(db, "eventReminders"), {
          eventId: mainEventRef.id,
          reminderTime: eventTimeIST,
          eventTitle: `Update Next Hearing : ${caseNo}`,
          caseNo,
          caseId: id,
          collection: collectionName,
          status: "pending",
          createdAt: new Date(),
          scheduledBy: globalUser.uid,
          recipientId: globalUser.uid,
        });
      }
    } catch (err) {
      console.error("Error recreating events for next hearing:", err);
    }
  };

  const handleSaveHearingDate = async () => {
    if (!editingHearingField) return;
    if (!caseCollection) {
      toast.error("Case collection unknown, cannot update.");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (editingHearingField === "next" && editingHearingDate) {
      if (editingHearingDate < today) {
        toast.error("Next hearing date cannot be in the past");
        return;
      }
      const prev = parseDMYToDate(caseData?.previousHearingDate);
      if (prev && editingHearingDate < prev) {
        toast.error("Next hearing date cannot be before the previous hearing date");
        return;
      }
    }
    if (editingHearingField === "previous" && editingHearingDate) {
      if (editingHearingDate > new Date()) {
        toast.error("Previous hearing date cannot be in the future");
        return;
      }
    }

    const newDmy = formatDateToDMY(editingHearingDate);
    setIsSavingHearingDate(true);

    try {
      const caseDocRef = doc(db, caseCollection, id);

      if (editingHearingField === "next") {
        const oldNext = caseData?.nextHearingDate || "";
        let updatedPrevious = caseData?.previousHearingDate || "";
        let caseHistory = Array.isArray(caseData?.caseHistory)
          ? [...caseData.caseHistory]
          : [];

        // If old next hearing was in past/today and changed, roll it into history & previous
        if (oldNext && oldNext !== newDmy) {
          const oldDate = parseDMYToDate(oldNext);
          if (oldDate) {
            oldDate.setHours(0, 0, 0, 0);
            if (oldDate <= today) {
              const already = caseHistory.some((h) => h.hearingDate === oldNext);
              if (!already) {
                caseHistory.push({
                  hearingDate: oldNext,
                  judge: "N/A",
                  purpose: "N/A",
                  causeListType: "N/A",
                  businessOnDate: "N/A",
                });
              }
              updatedPrevious = oldNext;
            }
          }
        }

        await updateDoc(caseDocRef, {
          nextHearingDate: newDmy,
          previousHearingDate: updatedPrevious,
          caseHistory,
        });

        setCaseData((prev) => ({
          ...prev,
          nextHearingDate: newDmy,
          previousHearingDate: updatedPrevious,
          caseHistory,
        }));

        addTimelineEvent(id, caseCollection, {
          type: "hearing_updated",
          title: "Hearing date updated",
          description: "Updated: Next Hearing Date",
          performedBy: globalUser?.uid || "",
          performedByName: globalUser?.name || globalUser?.displayName || "",
          changes: {
            "Next Hearing Date": {
              old: oldNext || "Not set",
              new: newDmy || "Not set",
            },
          },
        });

        await recreateEventsForNextHearing(newDmy);

        toast.success("Next hearing date updated");
        closeHearingEdit();
        setShowReminderInfoModal(true);
        setIsSavingHearingDate(false);
        return;
      } else {
        const oldPrev = caseData?.previousHearingDate || "";
        await updateDoc(caseDocRef, { previousHearingDate: newDmy });
        setCaseData((prev) => ({ ...prev, previousHearingDate: newDmy }));
        addTimelineEvent(id, caseCollection, {
          type: "case_updated",
          title: "Previous hearing date updated",
          description: "Updated: Previous Hearing Date",
          performedBy: globalUser?.uid || "",
          performedByName: globalUser?.name || globalUser?.displayName || "",
          changes: {
            "Previous Hearing Date": {
              old: oldPrev || "Not set",
              new: newDmy || "Not set",
            },
          },
        });
        toast.success("Previous hearing date updated");
      }

      closeHearingEdit();
    } catch (err) {
      console.error("Error updating hearing date:", err);
      toast.error("Failed to update hearing date");
    } finally {
      setIsSavingHearingDate(false);
    }
  };

  if (error) {
    return (
      <div className="cd-page min-h-screen flex flex-col items-center justify-center">
        <Navbar />
        <div className="text-red-600 text-lg p-8">{error}</div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Go Back
        </button>
      </div>
    );
  }

  const isLightMode = document.body.classList.contains("light-mode");

  if (loading || !caseData) {
    return (
      <SkeletonTheme
        baseColor={isLightMode ? "#d1d5db" : "#20203a"}
        highlightColor={isLightMode ? "#e5e7eb" : "#3a3a5e"}
      >
        <div className="crm-compact cd-page min-h-screen">
          <Navbar />
          <div className="cd-container">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Skeleton circle={true} height={24} width={24} className="mr-4" />
                <Skeleton height={28} width={300} />
              </div>
              <Skeleton circle={true} height={20} width={20} />
            </div>
            <div className="bg-white rounded-2xl p-6 border border-[#ECEEF5]">
              <div className="flex justify-between items-center mb-6">
                <Skeleton height={30} width={250} />
                <div className="flex items-center gap-4">
                  <Skeleton height={38} width={120} borderRadius={8} />
                  <Skeleton height={38} width={120} borderRadius={8} />
                  <Skeleton circle={true} height={20} width={20} />
                </div>
              </div>
              <hr className="border-gray-700 mb-8" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-6 mb-8">
                {[...Array(9)].map((_, i) => (
                  <div key={i}>
                    <Skeleton height={16} width={100} className="mb-1" />
                    <Skeleton height={20} width="80%" />
                  </div>
                ))}
              </div>
              <hr className="border-gray-700 mb-8" />
              <div className="mb-8">
                <div className="flex border-b border-gray-700">
                  <Skeleton height={40} width={100} className="mr-4" />
                  <Skeleton height={40} width={100} />
                </div>
                <div className="mt-6 bg-[#16213e] rounded-xl p-6">
                  <Skeleton height={20} width={150} className="mb-4" />
                  <Skeleton height={100} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </SkeletonTheme>
    );
  }

  // ---- light-CRM header helpers ----
  const parseDmyDate = (s) => {
    const m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec((s || "").trim());
    if (!m) return null;
    const d = new Date(+m[3], +m[2] - 1, +m[1]);
    return isNaN(d.getTime()) ? null : d;
  };
  const statusBucket = getStatusBucket(caseData?.status);
  const nextHearingParsed =
    statusBucket !== "disposed" ? parseDmyDate(caseData?.nextHearingDate) : null;
  // Hero cards (mockup top rows): team avatars + snapshot text.
  const heroTeam = Array.isArray(caseData?.teamMembers) ? caseData.teamMembers : [];
  const heroNotes = (caseData?.caseNotes || "").trim();

  // Save case notes from the hero "Notes" modal.
  const handleSaveNotes = async () => {
    if (!caseCollection) return;
    try {
      setSavingNotes(true);
      await updateDoc(doc(db, caseCollection, id), { caseNotes: notesDraft });
      setCaseData((prev) => ({ ...prev, caseNotes: notesDraft }));
      addTimelineEvent(id, caseCollection, {
        type: "notes_updated",
        title: "Case notes updated",
        description: "Case notes were updated",
        performedBy: globalUser?.uid,
        performedByName: "",
        changes: { "Case Notes": { old: "…", new: "Updated" } },
      });
      toast.success("Notes saved");
      setShowNotesModal(false);
    } catch (e) {
      console.error("Save notes:", e);
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  // ---- Case Progress steps (hero card + editor modal) ----
  // The stepper is start → user's custom steps → end. Start defaults to
  // "Filed" (case filing date) and end to "Disposed"; both can be renamed /
  // re-dated via `progressEndpoints` on the doc. Custom steps live in
  // `customProgressSteps`.
  const customProgressSteps = Array.isArray(caseData?.customProgressSteps)
    ? caseData.customProgressSteps
    : [];
  const progressEndpoints = caseData?.progressEndpoints || {};

  // Picked dates are stored as ISO (yyyy-mm-dd); render as "17 Jul 2026".
  const fmtStepDate = (d) => {
    if (!d) return null;
    const p = new Date(d);
    return isNaN(p.getTime())
      ? d
      : p.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  const dateToIso = (d) =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      : null;
  // Parse a stored step date (ISO) or a legacy dd/mm/yyyy case field into a
  // Date for the picker.
  const parseStepDate = (v) => {
    if (!v) return null;
    const m = String(v).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const d = m ? new Date(+m[3], +m[2] - 1, +m[1]) : new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const progressSteps = (() => {
    const disposedDone = statusBucket === "disposed";
    const steps = [
      {
        key: "start",
        label: progressEndpoints.startLabel || "Filed",
        rawDate: progressEndpoints.startDate || caseData?.caseFiledDate || null,
        dateText: progressEndpoints.startDate
          ? fmtStepDate(progressEndpoints.startDate)
          : caseData?.caseFiledDate || null,
        done: true,
        endpoint: true,
      },
      ...customProgressSteps.map((s) => ({
        key: s.id,
        id: s.id,
        label: s.label,
        rawDate: s.date,
        dateText: fmtStepDate(s.date),
        done: disposedDone || Boolean(s.done),
        custom: true,
      })),
      {
        key: "end",
        label: progressEndpoints.endLabel || "Disposed",
        rawDate: progressEndpoints.endDate || null,
        dateText: progressEndpoints.endDate ? fmtStepDate(progressEndpoints.endDate) : null,
        done: disposedDone,
        endpoint: true,
      },
    ];
    // done → green check, first not-done → current (amber), rest → todo.
    const firstTodo = steps.findIndex((s) => !s.done);
    return steps.map((s, i) => ({
      ...s,
      state: s.done ? "done" : i === firstTodo ? "current" : "todo",
    }));
  })();

  // Persist a new customProgressSteps array and optionally log a timeline event.
  const saveCustomSteps = async (nextSteps, timelineEvent) => {
    if (!caseCollection) return false;
    try {
      setSavingSteps(true);
      await updateDoc(doc(db, caseCollection, id), { customProgressSteps: nextSteps });
      setCaseData((prev) => ({ ...prev, customProgressSteps: nextSteps }));
      if (timelineEvent)
        addTimelineEvent(id, caseCollection, {
          performedBy: globalUser?.uid,
          performedByName: "",
          ...timelineEvent,
        });
      return true;
    } catch (e) {
      console.error("Save progress steps:", e);
      toast.error("Failed to update progress steps");
      return false;
    } finally {
      setSavingSteps(false);
    }
  };

  const handleAddCustomStep = async () => {
    const label = newStepLabel.trim();
    if (!label) {
      toast.error("Select a status for the step");
      return;
    }
    const step = { id: `step_${Date.now()}`, label, date: dateToIso(newStepDate), done: false };
    const ok = await saveCustomSteps([...customProgressSteps, step], {
      type: "progress_step_added",
      title: "Progress step added",
      description: `Step "${label}" added to case progress`,
    });
    if (ok) {
      setNewStepLabel("");
      setNewStepDate(null);
      toast.success("Step added");
    }
  };

  const handleToggleCustomStep = (stepId) => {
    const next = customProgressSteps.map((s) =>
      s.id === stepId ? { ...s, done: !s.done } : s
    );
    const toggled = next.find((s) => s.id === stepId);
    saveCustomSteps(
      next,
      toggled?.done
        ? {
            type: "progress_step_completed",
            title: "Progress step completed",
            description: `Step "${toggled.label}" marked as completed`,
          }
        : null
    );
  };

  const handleDeleteCustomStep = (stepId) => {
    const removed = customProgressSteps.find((s) => s.id === stepId);
    saveCustomSteps(
      customProgressSteps.filter((s) => s.id !== stepId),
      removed
        ? {
            type: "progress_step_removed",
            title: "Progress step removed",
            description: `Step "${removed.label}" removed from case progress`,
          }
        : null
    );
  };

  const handleMoveCustomStep = (stepId, dir) => {
    const i = customProgressSteps.findIndex((s) => s.id === stepId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= customProgressSteps.length) return;
    const next = [...customProgressSteps];
    [next[i], next[j]] = [next[j], next[i]];
    saveCustomSteps(next, null);
  };

  // Inline edit (rename / re-date) of any step, including the start and end
  // endpoints. Endpoint overrides go to `progressEndpoints`; custom steps are
  // updated in place.
  const openStepEdit = (step) => {
    setEditingStepKey(step.key);
    setStepLabelDraft(step.label);
    setStepDateDraft(parseStepDate(step.rawDate));
  };

  const handleSaveStepEdit = async () => {
    const label = stepLabelDraft.trim();
    if (!label) {
      toast.error("Step name required");
      return;
    }
    const iso = dateToIso(stepDateDraft);
    if (editingStepKey === "start" || editingStepKey === "end") {
      if (!caseCollection) return;
      const next = {
        ...progressEndpoints,
        [`${editingStepKey}Label`]: label,
        [`${editingStepKey}Date`]: iso,
      };
      try {
        setSavingSteps(true);
        await updateDoc(doc(db, caseCollection, id), { progressEndpoints: next });
        setCaseData((prev) => ({ ...prev, progressEndpoints: next }));
        addTimelineEvent(id, caseCollection, {
          type: "progress_step_updated",
          title: "Progress step updated",
          description: `Step "${label}" updated in case progress`,
          performedBy: globalUser?.uid,
          performedByName: "",
        });
        toast.success("Step updated");
        setEditingStepKey(null);
      } catch (e) {
        console.error("Save endpoint step:", e);
        toast.error("Failed to update step");
      } finally {
        setSavingSteps(false);
      }
    } else {
      const next = customProgressSteps.map((s) =>
        s.id === editingStepKey ? { ...s, label, date: iso } : s
      );
      const ok = await saveCustomSteps(next, {
        type: "progress_step_updated",
        title: "Progress step updated",
        description: `Step "${label}" updated in case progress`,
      });
      if (ok) {
        setEditingStepKey(null);
        toast.success("Step updated");
      }
    }
  };

  const caseNoDisplay = isSupremeCourtCase
    ? (caseData?.payload?.caseNumber || caseData?.caseNumber || "N/A")
    : [caseData?.caseNumber, caseData?.caseType, caseData?.caseYear].filter(Boolean).join("/") || "N/A";
  const isClient = localStorage.getItem("userType") === "client";
  // Switch tab + bring the tab strip into view (used by the right rail).
  const openTab = (tab) => {
    setActiveTab(tab);
    tabsScrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // ---- Case Information rows (Overview grid + PDF export). Same
  // conditional field set the old details table rendered, per court type. ----
  const buildInfoRows = () => {
    const rows = [];
    const Row = (label, value, key) =>
      rows.push({
        label,
        value,
        key,
        text: typeof value === "string" ? value : undefined,
      });

                  if (!isSupremeCourtCase && caseData?.filingNumber) Row("Filing Number", caseData.filingNumber, "filingnum");
                  if (isSupremeCourtCase && caseData?.payload?.cnrNumber) Row("CNR Number", caseData.payload.cnrNumber, "cnr");
                  if (isSupremeCourtCase && caseData?.payload?.section) Row("Section", caseData.payload.section, "section");
                  Row("Party Contact Number", caseData?.partyContactNumber || "N/A", "partycontact");
                  Row("Filing Date", caseData?.caseFiledDate || "N/A", "filingdate");
                  if (!isSupremeCourtCase) Row("Case Type", caseData?.caseType || "N/A", "casetype");
                  if (!isSupremeCourtCase) Row("Case Year", caseData?.caseYear || "N/A", "caseyear");
                  Row("Case Status", caseData?.status ? statusLabel(caseData.status) : "N/A", "status");
                  if (caseData?.caseStage) Row("Case Stage", caseData.caseStage, "casestage");
                  if (isNcltCase && ncltCaseStage) Row("Case Stage", ncltCaseStage, "ncltstage");
                  if (getStatusBucket(caseData?.status) !== "disposed") Row("Next Hearing Date", (
                    <span className="flex items-center gap-2">
                      <span>{caseData?.nextHearingDate || "N/A"}</span>
                      <button onClick={() => openHearingEdit("next")} className="text-yellow-500 hover:text-yellow-400 transition-colors" title="Edit next hearing date">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => setShowEventsModal(true)} className="text-yellow-500 hover:text-yellow-400 transition-colors" title="View case events">
                        <AlarmClock size={18} />
                      </button>
                    </span>
                  ), "nexthearing");
                  if (!isNgtCase) Row("Previous Hearing Date", (
                    <span className="flex items-center gap-2">
                      <span>{caseData?.previousHearingDate || "N/A"}</span>
                      <button onClick={() => openHearingEdit("previous")} className="text-yellow-500 hover:text-yellow-400 transition-colors" title="Edit previous hearing date">
                        <Pencil size={16} />
                      </button>
                    </span>
                  ), "prevhearing");
                  Row("Court Name", caseData.court || "N/A", "court");
                  if (isSupremeCourtCase && (caseData?.payload?.category || caseData?.caseType))
                    Row("Category", caseData.payload?.category || caseData.caseType, "category");
                  if (isSupremeCourtCase && caseData?.payload?.statusStage)
                    Row("Status / Stage", caseData.payload.statusStage, "stage");
                  if (isNgtCase && (ngtInfo.act || caseData?.caseType)) Row("Act", ngtInfo.act || caseData?.caseType || "N/A", "ngtact");
                  if (isNgtCase && (ngtInfo.lastListed || caseData?.previousHearingDate)) Row("Last Listed", ngtInfo.lastListed || caseData?.previousHearingDate || "N/A", "ngtlast");
                  if (isNgtCase && ngtInfo.zonalBench) Row("Zonal Bench", ngtInfo.zonalBench, "ngtzonal");
                  if (isNgtCase && (ngtInfo.diaryNumber || ngtInfo.filingNumber || caseData?.filingNumber)) Row("Diary / Filing No", ngtInfo.diaryNumber || ngtInfo.filingNumber || caseData?.filingNumber, "ngtdiary");
                  if (isNgtCase && ngtInfo.registeredOn) Row("Registered On", ngtInfo.registeredOn, "ngtreg");
                  // Consumer Court (e-Jagriti) specific rows
                  if (isConsumerCourtCase && (caseData?.cino || consumerInfo.referenceNumber)) Row("Filing Reference Number", caseData?.cino || consumerInfo.referenceNumber, "consref");
                  if (isConsumerCourtCase && consumerInfo.caseStage) Row("Case Stage", consumerInfo.caseStage, "consstage");
                  if (isConsumerCourtCase && consumerInfo.filedIn) Row("Filed In", consumerInfo.filedIn, "consfiledin");
                  if (isConsumerCourtCase && consumerInfo.filedBy && consumerInfo.filedBy !== "N/A") Row("Filed By", consumerInfo.filedBy, "consfiledby");
                  if (isConsumerCourtCase && consumerInfo.purpose && consumerInfo.purpose !== "N/A") Row("Purpose", consumerInfo.purpose, "conspurpose");
                  if (isConsumerCourtCase && Array.isArray(consumerInfo.applicationFiled) && consumerInfo.applicationFiled.length) Row("Application Filed", consumerInfo.applicationFiled.join(", "), "consapp");
                  if (isConsumerCourtCase && consumerInfo.attachedLowerCourt && consumerInfo.attachedLowerCourt !== "N/A") Row("Attached Lower Court", consumerInfo.attachedLowerCourt, "conslower");
                  if (isUpReraCase && upReraInfo.complaintAgainst) Row("Complaint Against", upReraInfo.complaintAgainst, "uprca");
                  if (isUpReraCase && upReraInfo.projectDetails) Row("Project Details", upReraInfo.projectDetails, "uprproj");
                  if (isUpReraCase && upReraInfo.competentAuthority) Row("Competent Authority", upReraInfo.competentAuthority, "uprauth");
                  if (isUpReraCase && upReraInfo.district) Row("District", upReraInfo.district, "uprdist");
                  if (isUpReraCase && upReraInfo.hearingDetails) Row("Hearing Details", upReraInfo.hearingDetails, "uprhearing");
                  if (isUpReraCase && upReraInfo.complainantAddress) Row("Complainant Address", upReraInfo.complainantAddress, "upraddr");
                  if (isUpReraCase && upReraInfo.complainantMobile) Row("Complainant Mobile", upReraInfo.complainantMobile, "uprmob");
                  if (isUpReraCase && upReraInfo.complainantEmail) Row("Complainant Email", upReraInfo.complainantEmail, "upremail");
                  if (isUpReraCase && upReraInfo.apartmentPlotNo) Row("Apartment / Plot No", upReraInfo.apartmentPlotNo, "uprplot");
                  if (isUpReraCase && upReraInfo.totalValue) Row("Total Value", upReraInfo.totalValue, "uprval");
                  if (isUpReraCase && upReraInfo.amountPaidTillDate) Row("Amount Paid Till Date", upReraInfo.amountPaidTillDate, "uprpaid");
                  if (isUpReraCase && upReraInfo.reliefSoughtType) Row("Relief Sought Type", upReraInfo.reliefSoughtType, "uprrelief");
                  if (isUpReraCase && upReraInfo.gracePeriod) Row("Grace Period", upReraInfo.gracePeriod, "uprgrace");
                  if (isUpReraCase && upReraInfo.interimReliefRequest) Row("Interim Relief Request", upReraInfo.interimReliefRequest, "uprintrelief");
                  if (isUpReraCase && upReraInfo.feePaid) Row("Fee Paid", upReraInfo.feePaid, "uprfee");
                  if (isUpReraCase && upReraInfo.feePaymentDate) Row("Fee Payment Date", upReraInfo.feePaymentDate, "uprfeedate");
                  if (isUpReraCase && upReraInfo.noticeUrl) Row("Notice", <a href={upReraInfo.noticeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline hover:text-blue-300">View Notice</a>, "uprnotice");
                  if (caseData?.authorityDepartment) Row("Authority Department", caseData.authorityDepartment === "Others" ? caseData.authorityDepartmentOther || "Others" : caseData.authorityDepartment, "authdept");
                  if (caseData?.properties) Row("Properties", caseData.properties, "props");
                  if (caseData?.area) Row("Area", caseData.area, "area");
                  if (caseData?.village) Row("Village / Division", caseData.village, "village");
                  if (caseData?.stayOrderDate) Row("Stay Order Date", caseData.stayOrderDate, "stay");
                  if (caseData?.counterAffidavitStatus) Row("Counter Affidavit Status", caseData.counterAffidavitStatus, "counteraff");
                  if (caseData?.counterAppealFiled) Row("Counter Appeal Filed", caseData.counterAppealFiled, "counterapp");
                  if (caseData?.prayers) Row("Prayers", caseData.prayers, "prayers");
                  Row("Shared to", teamNames, "shared");
                  Row("Petitioner(s)", (
                    <span>
                      {caseData.petitionerName || "N/A"}
                      {caseData.petitionerRelation && caseData.petitionerRelationName && (
                        <span className="text-gray-300"> {caseData.petitionerRelation} {caseData.petitionerRelationName}</span>
                      )}
                      {getOtherPetitioners() && <span className="block mt-1">{getOtherPetitioners()}</span>}
                    </span>
                  ), "petitioner");
                  if (caseData.petitionerAdvocate || getOtherPetitionerAdvocates())
                    Row("Petitioner Advocate(s)", (
                      <span>
                        {caseData.petitionerAdvocate}
                        {getOtherPetitionerAdvocates() && <span className="block mt-1">{getOtherPetitionerAdvocates()}</span>}
                      </span>
                    ), "petadv");
                  Row("Respondent(s)", (
                    <span>
                      {caseData.respondentName || "N/A"}
                      {caseData.respondentRelation && caseData.respondentRelationName && (
                        <span className="text-gray-300"> {caseData.respondentRelation} {caseData.respondentRelationName}</span>
                      )}
                      {getOtherRespondents() && <span className="block mt-1">{getOtherRespondents()}</span>}
                    </span>
                  ), "respondent");
                  if (caseData.respondentAdvocate || getOtherRespondentAdvocates())
                    Row("Respondent Advocate(s)", (
                      <span>
                        {caseData.respondentAdvocate}
                        {getOtherRespondentAdvocates() && <span className="block mt-1">{getOtherRespondentAdvocates()}</span>}
                      </span>
                    ), "respadv");
    return rows;
  };
  const infoRows = caseData ? buildInfoRows() : [];

  // Full Case Information / Petitioner-Respondent / About panels — shown in
  // the "View Full Details" modal (same content the Overview tab used to render).
  const renderFullCaseInfo = () => {
    const partyOrder = [
      "petitioner(s)", "petitioner advocate(s)", "respondent(s)",
      "respondent advocate(s)", "party contact number", "shared to",
    ];
    const isParty = (r) => partyOrder.includes(r.label.toLowerCase());
    const isStatusCol = (r) => /status|stage|hearing|priority/i.test(r.label) || r.label === "Court Name";
    const col1 = infoRows
      .filter(isParty)
      .sort((a, b) => partyOrder.indexOf(a.label.toLowerCase()) - partyOrder.indexOf(b.label.toLowerCase()));
    const rest = infoRows.filter((r) => !isParty(r));
    const col3 = rest.filter(isStatusCol);
    const col2 = rest.filter((r) => !isStatusCol(r));
    const renderItem = (r) => (
      <div className="cd-info-item" key={r.key}>
        <span className="cd-info-icon">{infoIconFor(r.label)}</span>
        <div style={{ minWidth: 0 }}>
          <p className="cd-info-label">{r.label}</p>
          <div className="cd-info-value">
            {r.label === "Case Status" ? (
              <span className={`cd-pill ${statusBucket === "disposed" ? "cd-pill-green" : "cd-pill-amber"}`}>
                {r.value}
              </span>
            ) : r.label === "Case Stage" ? (
              <span className="cd-pill cd-pill-amber">{r.value}</span>
            ) : (
              r.value
            )}
          </div>
        </div>
      </div>
    );
    const initialsOf = (n = "") =>
      n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";
    const countOf = (others) =>
      others ? String(others).split(",").filter((s) => s.trim()).length : 0;
    const otherPet = getOtherPetitioners();
    const otherResp = getOtherRespondents();
    const aboutText = String(caseData?.prayers || caseData?.caseNotes || "").trim();
    return (
      <>
        <div className="cd-panel">
          <h3 className="cd-info-title">Case Information</h3>
          <div className="cd-info-grid">
            <div className="cd-info-col">{col1.map(renderItem)}</div>
            <div className="cd-info-col">{col2.map(renderItem)}</div>
            <div className="cd-info-col">{col3.map(renderItem)}</div>
          </div>
        </div>

        <div className="cd-parties">
          <div className="cd-panel">
            <h3 className="cd-party-title">Petitioner ({1 + countOf(otherPet)})</h3>
            <div className="cd-party-row">
              <span className="cd-party-avatar" style={{ background: "#DCFCE7", color: "#15803D" }}>
                {initialsOf(caseData?.petitionerName)}
              </span>
              <div style={{ minWidth: 0 }}>
                <p className="cd-party-name">{caseData?.petitionerName || "N/A"}</p>
                {caseData?.petitionerAdvocate && (
                  <p className="cd-party-sub">Adv. {caseData.petitionerAdvocate}</p>
                )}
              </div>
            </div>
            {otherPet && (
              <p className="cd-party-sub" style={{ marginTop: 10 }}>Others: {otherPet}</p>
            )}
          </div>
          <div className="cd-panel">
            <h3 className="cd-party-title">Respondent ({1 + countOf(otherResp)})</h3>
            <div className="cd-party-row">
              <span className="cd-party-avatar" style={{ background: "#EEF2FF", color: "#4F46E5" }}>
                {initialsOf(caseData?.respondentName)}
              </span>
              <div style={{ minWidth: 0 }}>
                <p className="cd-party-name">{caseData?.respondentName || "N/A"}</p>
                {caseData?.respondentAdvocate && (
                  <p className="cd-party-sub">Adv. {caseData.respondentAdvocate}</p>
                )}
              </div>
            </div>
            {otherResp && (
              <p className="cd-party-sub" style={{ marginTop: 10 }}>Others: {otherResp}</p>
            )}
          </div>
        </div>

        {aboutText && (
          <div className="cd-panel cd-about">
            <h3 className="cd-info-title">About the Case</h3>
            <p className="cd-about-text">
              {aboutExpanded || aboutText.length <= 280 ? aboutText : `${aboutText.slice(0, 280)}…`}
            </p>
            {aboutText.length > 280 && (
              <button className="cd-about-more" onClick={() => setAboutExpanded((v) => !v)}>
                {aboutExpanded ? "Show Less" : "Read More"}
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  // Case summary PDF from the same rows the Overview grid shows. Rows whose
  // value is JSX fall back to plain-text equivalents by key.
  const downloadCasePdf = () => {
    try {
      const pdfTextFallback = {
        nexthearing: caseData?.nextHearingDate,
        prevhearing: caseData?.previousHearingDate,
        petitioner: [caseData?.petitionerName, getOtherPetitioners()].filter(Boolean).join("; "),
        petadv: [caseData?.petitionerAdvocate, getOtherPetitionerAdvocates()].filter(Boolean).join("; "),
        respondent: [caseData?.respondentName, getOtherRespondents()].filter(Boolean).join("; "),
        respadv: [caseData?.respondentAdvocate, getOtherRespondentAdvocates()].filter(Boolean).join("; "),
      };
      const pdf = new jsPDF();
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      let y = 18;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      const title = `${caseData?.petitionerName || "N/A"} vs ${caseData?.respondentName || "N/A"}`;
      const titleLines = pdf.splitTextToSize(title, pageW - 28);
      pdf.text(titleLines, 14, y);
      y += titleLines.length * 6 + 2;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      const subLines = pdf.splitTextToSize(
        `${caseNoDisplay}  ·  ${caseData?.court || ""}`,
        pageW - 28
      );
      pdf.text(subLines, 14, y);
      y += subLines.length * 5 + 3;
      pdf.setDrawColor(200);
      pdf.line(14, y, pageW - 14, y);
      y += 8;

      infoRows.forEach((r) => {
        const raw = r.text !== undefined ? r.text : pdfTextFallback[r.key];
        if (raw === undefined || raw === null || raw === "") return;
        const valLines = pdf.splitTextToSize(String(raw), pageW - 78);
        const blockH = Math.max(valLines.length * 5, 5) + 3;
        if (y + blockH > pageH - 14) {
          pdf.addPage();
          y = 18;
        }
        pdf.setTextColor(120);
        pdf.text(String(r.label), 14, y);
        pdf.setTextColor(20);
        pdf.text(valLines, 62, y);
        y += blockH;
      });

      pdf.save(`Case_${String(caseNoDisplay || id).replace(/[/\\\s]+/g, "_")}.pdf`);
      toast.success("Case PDF downloaded");
    } catch (e) {
      console.error("Case PDF error:", e);
      toast.error("Failed to generate PDF");
    }
  };

  const goBackToCases = () => {
    if (fromPage === 'allcases') {
      navigate('/portal/AllCases');
    } else if (fromPage === 'pending') {
      navigate('/portal/PendingCases/pending');
    } else if (fromPage === 'disposed') {
      navigate('/portal/DisposedCases/disposed');
    } else if (fromPage === 'myfiles') {
      navigate('/portal/MyFiles');
    } else {
      navigate('/portal/AllCases');
    }
  };

  return (
    <div className="crm-compact cd-page relative">
      <div>
      <Navbar />

      <div className="cd-container">
        {/* Lock Banner */}
        {isLocked && (
          <div className="cd-lock-banner">
            <div className="flex items-center gap-3">
              <Lock size={20} color="#B91C1C" />
              <div>
                <h3>This Case is Locked</h3>
                <p>
                  This case was added during your subscription period and is now locked because your subscription has expired.
                </p>
              </div>
            </div>
            <Link
              to="/portal/subscription"
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-1.5 px-4 rounded-lg transition whitespace-nowrap text-sm"
            >
              Upgrade Plan
            </Link>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="cd-breadcrumb">
          <button onClick={goBackToCases}>Cases</button>
          <ChevronRight size={13} />
          <span className="cd-crumb-current">Case Details</span>
        </div>

        {/* Title row */}
        <div className="cd-title-row">
          <div className="cd-title-left">
            <h1 className="cd-title">
              {`${caseData?.petitionerName || "N/A"} vs ${caseData?.respondentName || "N/A"}`}
            </h1>
            <span
              className={`cd-status-pill ${statusBucket === "disposed" ? "cd-status-disposed" : "cd-status-pending"}`}
            >
              {statusLabel(caseData?.status) || "Pending"}
            </span>
            {caseLabels.map((label) => (
              <span
                key={label.id}
                className="cd-label-chip"
                style={{ backgroundColor: label.color }}
              >
                {label.name}
              </span>
            ))}
            {isLocked && (
              <span title="Case is locked due to expired subscription">
                <Lock size={16} color="#9CA3AF" />
              </span>
            )}
          </div>
          <div className="cd-actions">
            {isShared && !isOwner && (
              <>
                <button
                  onClick={handleExportCase}
                  disabled={isExporting}
                  className="cd-btn cd-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      Save
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowExportInfoModal(true)}
                  className="cd-btn-icon"
                  title="What does Save do?"
                >
                  <Info size={16} />
                </button>
              </>
            )}
            <button className="cd-btn" onClick={downloadCasePdf}>
              <FileDown size={14} />
              Download PDF
            </button>
            {statusBucket !== "disposed" && !isLocked && (
              <Link to={`/portal/case/${id}/edit`}>
                <button className="cd-btn cd-btn-gold">
                  <Edit size={14} />
                  Edit Case
                </button>
              </Link>
            )}
            <div className="cd-menu">
              <button
                className="cd-btn-icon"
                title="More actions"
                onClick={() => setShowHeaderMenu((v) => !v)}
              >
                <MoreVertical size={16} />
              </button>
              {showHeaderMenu && (
                <>
                  <div
                    className="cd-menu-overlay"
                    onClick={() => setShowHeaderMenu(false)}
                  />
                  <div className="cd-menu-pop">
                    <button
                      className="cd-menu-item"
                      onClick={() => {
                        setShowHeaderMenu(false);
                        navigate(`/portal/case/${id}/case-files${fromPage ? `?from=${fromPage}` : ''}`);
                      }}
                    >
                      <FolderOpen size={14} />
                      View Files
                    </button>
                    {isOwner && statusBucket !== "disposed" && (
                      <button
                        className="cd-menu-item cd-menu-danger"
                        onClick={() => {
                          setShowHeaderMenu(false);
                          setShowDisposeModal(true);
                        }}
                      >
                        <AlarmClock size={14} />
                        Dispose Case
                      </button>
                    )}
                    {isOwner && (
                      <button
                        className="cd-menu-item cd-menu-danger"
                        onClick={() => {
                          setShowHeaderMenu(false);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash size={14} />
                        Delete Case
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Case number + court */}
        <div className="cd-subline">
          <span className="cd-caseno">{caseNoDisplay}</span>
          {isSupremeCourtCase && caseData?.payload?.diaryNumber && (
            <>
              <span className="cd-dot">•</span>
              <span>Diary No: {caseData.payload.diaryNumber}</span>
            </>
          )}
          {caseData?.court && (
            <>
              <span className="cd-dot">•</span>
              <span className="cd-court">
                <MapPin size={13} />
                {caseData.court}
              </span>
            </>
          )}
        </div>

        <div className="cd-layout">
        <div className="cd-main">
        {/* ===== Hero row 1: Next Hearing (left) | Case Details | Case Progress ===== */}
        <div className="cd-hero3">
          {/* Next Hearing */}
          <div className="cd-panel cd-hero-panel">
            <div className="cd-panel-head">
              <h3 className="cd-panel-title">
                <CalendarDays size={14} className="cd-title-ic ic-amber" />
                Next Hearing
              </h3>
              {statusBucket !== "disposed" && !isLocked && (
                <div className="cd-head-actions">
                  <button onClick={() => openHearingEdit("next")} title="Edit next hearing date">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => setShowEventsModal(true)} title="View case events & reminders">
                    <AlarmClock size={14} />
                  </button>
                </div>
              )}
            </div>
            {nextHearingParsed ? (
              <div className="cd-nh-date">
                <div className="cd-nh-day">{nextHearingParsed.getDate()}</div>
                <div>
                  <div className="cd-nh-month">
                    {nextHearingParsed.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                  </div>
                  <div className="cd-nh-weekday">
                    {nextHearingParsed.toLocaleDateString("en-GB", { weekday: "long" })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="cd-nh-empty">
                {statusBucket === "disposed"
                  ? "Case disposed"
                  : caseData?.nextHearingDate || "No hearing date set"}
              </div>
            )}
            <div className="cd-nh-meta">
              {caseData?.court && (
                <p>
                  <MapPin size={13} />
                  <span title={caseData.court}>{caseData.court}</span>
                </p>
              )}
              {caseData?.previousHearingDate && (
                <p>
                  <CalendarDays size={13} />
                  <span>Prev. hearing: {caseData.previousHearingDate}</span>
                </p>
              )}
            </div>
            <button className="cd-panel-footer-btn" onClick={() => setShowEventsModal(true)}>
              View Hearing Details <ChevronRight size={14} />
            </button>
          </div>

          {/* Case Details */}
          <div className="cd-panel cd-hero-panel">
            <div className="cd-panel-head">
              <h3 className="cd-panel-title">
                <Info size={14} className="cd-title-ic ic-indigo" />
                Case Details
              </h3>
            </div>
            <div className="cd-dt-grid">
              {[
                { l: "Petitioner", v: caseData?.petitionerName },
                { l: "Respondent", v: caseData?.respondentName },
                {
                  l: isSupremeCourtCase && caseData?.payload?.cnrNumber ? "CNR Number" : "Filing Number",
                  v: isSupremeCourtCase ? caseData?.payload?.cnrNumber : caseData?.filingNumber || caseNoDisplay,
                },
                { l: "Filing Date", v: caseData?.caseFiledDate },
                { l: "Court", v: caseData?.court },
                { l: "Case Type", v: caseData?.caseType },
                { l: "Case Year", v: caseData?.caseYear },
                { l: "Stage", v: caseData?.caseStage },
                { l: "Advocate", v: caseData?.petitionerAdvocate || caseData?.respondentAdvocate },
                { l: "Contact", v: caseData?.partyContactNumber },
                { l: "Status", v: statusLabel(caseData?.status) },
                { l: "Shared To", v: teamNames !== "No teams" ? teamNames : "" },
              ]
                .filter((r) => r.v)
                .slice(0, 12)
                .map((r) => (
                  <div className="cd-dt-pair" key={r.l}>
                    <span className="cd-dt-label">{r.l}</span>
                    <span className="cd-dt-value" title={String(r.v)}>{r.v}</span>
                  </div>
                ))}
            </div>
            <button className="cd-panel-footer-btn" onClick={() => setShowFullDetailsModal(true)}>
              View Full Details <ChevronRight size={14} />
            </button>
          </div>

          {/* Case Progress */}
          <div className="cd-panel cd-hero-panel">
            <div className="cd-panel-head">
              <h3 className="cd-panel-title">
                <Tag size={14} className="cd-title-ic ic-orange" />
                Case Progress
              </h3>
              {!isClient && !isLocked && (
                <div className="cd-head-actions">
                  <button onClick={() => setShowProgressModal(true)} title="View & edit progress steps">
                    <Pencil size={13} />
                  </button>
                </div>
              )}
            </div>
            <div
              className="cd-steps cd-steps-clickable"
              onClick={() => setShowProgressModal(true)}
              role="button"
              title="View & edit progress steps"
            >
              {progressSteps.map((s) => (
                <div key={s.key} className={`cd-step ${s.state}`}>
                  <span className="cd-step-dot">
                    {s.state === "done" && <Check size={11} strokeWidth={3} />}
                  </span>
                  <p className="cd-step-label" title={s.label}>{s.label}</p>
                  <p className="cd-step-date">
                    {s.dateText || (s.state === "todo" ? "Pending" : "—")}
                  </p>
                </div>
              ))}
            </div>
            <div className="cd-progress-status">
              <span>Status</span>
              <span
                className={`cd-status-pill ${statusBucket === "disposed" ? "cd-status-disposed" : "cd-status-pending"}`}
              >
                {statusLabel(caseData?.status) || "Pending"}
              </span>
            </div>
          </div>
        </div>

        {/* ===== Hero row 2: Assigned Team | Case Snapshot (case notes) | Recent Activity ===== */}
        <div className="cd-row2">
          {/* Assigned Team */}
          <div className="cd-panel cd-hero-panel">
            <div className="cd-panel-head">
              <h3 className="cd-panel-title">
                <Users size={14} className="cd-title-ic ic-purple" />
                Assigned Team
              </h3>
            </div>
            {heroTeam.length === 0 ? (
              <p className="cd-rail-muted">Not shared with anyone yet</p>
            ) : (
              <div className="cd-avatars" style={{ marginBottom: 12 }}>
                {heroTeam.slice(0, 5).map((m) => {
                  const c = heroAvatarColor(m.name);
                  return (
                    <span
                      key={m.uid}
                      className="cd-avatar"
                      style={{ background: c.bg, color: c.fg }}
                      title={m.name}
                    >
                      {heroInitials(m.name)}
                    </span>
                  );
                })}
                {heroTeam.length > 5 && (
                  <span className="cd-avatar cd-avatar-more">+{heroTeam.length - 5}</span>
                )}
              </div>
            )}
            <button className="cd-panel-footer-btn" onClick={() => openTab("allow-access")}>
              View Team <ChevronRight size={14} />
            </button>
          </div>

          {/* Notes — the case notes; edited inline via a modal */}
          <div className="cd-panel cd-hero-panel">
            <div className="cd-panel-head">
              <h3 className="cd-panel-title">
                <StickyNote size={14} className="cd-title-ic ic-purple" />
                Notes
              </h3>
            </div>
            <p className="cd-snapshot-text">
              {heroNotes || "No case notes yet — click Edit Notes to add where this matter stands."}
            </p>
            <div className="cd-snap-actions">
              <button
                className="cd-snap-btn"
                onClick={() => {
                  setNotesDraft(caseData?.caseNotes || "");
                  setShowNotesModal(true);
                }}
              >
                <Pencil size={12} /> Edit Notes
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="cd-panel cd-hero-panel">
            <div className="cd-panel-head">
              <h3 className="cd-panel-title">
                <History size={14} className="cd-title-ic ic-blue" />
                Recent Activity
              </h3>
              <button className="cd-rail-link" style={{ marginTop: 0 }} onClick={() => openTab("timeline")}>
                View All
              </button>
            </div>
            {heroActivity.length === 0 ? (
              <p className="cd-rail-muted">No activity recorded yet</p>
            ) : (
              <div className="cd-activity-list">
                {heroActivity.map((ev) => (
                  <div className="cd-activity-item" key={ev.id}>
                    <div className="cd-activity-icon">
                      <History size={12} />
                    </div>
                    <div className="cd-activity-body">
                      <p>{ev.title || ev.description || "Update"}</p>
                      <span>
                        {[ev.performedByName, timeAgo(ev.timestamp)].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
        <CaseDetailsRail
          caseId={id}
          caseData={caseData}
          caseCollection={caseCollection}
          statusBucket={statusBucket}
          isOwner={isOwner}
          isClient={isClient}
          isLocked={isLocked}
          nextHearingParsed={nextHearingParsed}
          onAddHearing={() => setShowAddEventModal(true)}
          onViewEvents={() => setShowEventsModal(true)}
          onOpenTab={openTab}
          onEditNotes={() => {
            setNotesDraft(caseData?.caseNotes || "");
            setShowNotesModal(true);
          }}
          onSummarizeAllOrders={handleSummarizeAllOrders}
        />
        </div>

        {/* ===== Tabs & tab content — full width, below the hero + rail ===== */}
        <div>
          {/* ===== OLD GRID LAYOUT (disabled, kept for easy revert — change `false` to `true` to restore) ===== */}
          {false && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-3 mb-8">
            <div>
              <span className="text-gray-400 text-sm">Party Contact Number: </span>
              <span className="text-white">{caseData?.partyContactNumber || "N/A"}</span>
            </div>
            {getStatusBucket(caseData?.status) !== "disposed" && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Next Hearing Date: </span>
                <span className="text-white">{caseData?.nextHearingDate || "N/A"}</span>
                <button
                  onClick={() => openHearingEdit("next")}
                  className="text-yellow-500 hover:text-yellow-400 transition-colors"
                  title="Edit next hearing date"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => setShowEventsModal(true)}
                  className="text-yellow-500 hover:text-yellow-400 transition-colors"
                  title="View case events"
                >
                  <AlarmClock size={18} />
                </button>
              </div>
            )}
            <div>
              <span className="text-gray-400 text-sm">Shared to: </span>
              <span className="text-white">{teamNames}</span>
            </div>
            {/* For NGT, the standard "Previous hearing date" is hidden — the
                dedicated "Last Listed" row below shows the same value. */}
            {!isNgtCase && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Previous hearing date: </span>
                <span className="text-white">{caseData?.previousHearingDate || "N/A"}</span>
                <button
                  onClick={() => openHearingEdit("previous")}
                  className="text-yellow-500 hover:text-yellow-400 transition-colors"
                  title="Edit previous hearing date"
                >
                  <Pencil size={16} />
                </button>
              </div>
            )}
            {!isSupremeCourtCase && (
              <div>
                <span className="text-gray-400 text-sm">Case Type: </span>
                <span className="text-white break-words">{caseData?.caseType || "N/A"}</span>
              </div>
            )}
            <div>
              <span className="text-gray-400 text-sm">Filing Date: </span>
              <span className="text-white">{caseData?.caseFiledDate || "N/A"}</span>
            </div>
            {!isSupremeCourtCase && (
              <div>
                <span className="text-gray-400 text-sm">Case Year: </span>
                <span className="text-white">{caseData?.caseYear || "N/A"}</span>
              </div>
            )}
            {isSupremeCourtCase && caseData?.payload?.section && (
              <div>
                <span className="text-gray-400 text-sm">Section: </span>
                <span className="text-white">{caseData.payload.section}</span>
              </div>
            )}
            {isSupremeCourtCase && caseData?.payload?.cnrNumber && (
              <div>
                <span className="text-gray-400 text-sm">CNR Number: </span>
                <span className="text-white">{caseData.payload.cnrNumber}</span>
              </div>
            )}
            <div>
              <span className="text-gray-400 text-sm">Case Status: </span>
              <span className="text-white">
                {statusLabel(caseData?.status)}
              </span>
            </div>
            {caseData?.caseStage && (
              <div>
                <span className="text-gray-400 text-sm">Case Stage: </span>
                <span className="text-white">{caseData.caseStage}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">Case Files </span>
              <div className="flex gap-2">
                <Link to={`/portal/case/${id}/case-files${fromPage ? `?from=${fromPage}` : ''}`}>
                  <button className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-1 rounded-full text-sm">
                    View
                  </button>
                </Link>
              </div>
            </div>
            <div className="md:col-span-2 lg:col-span-1">
              <span className="text-gray-400 text-sm">Court Name: </span>
              <span className="text-white">{caseData.court || "N/A"}</span>
            </div>
            {isSupremeCourtCase && (caseData?.payload?.category || caseData?.caseType) && (() => {
              const fullText = caseData.payload?.category || caseData.caseType;
              const isLong = String(fullText).length > 70;
              return (
                <div className="lg:col-span-1 relative">
                  <p
                    className="text-white break-words overflow-hidden"
                    style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                  >
                    <span className="text-gray-400 text-sm">Category: </span>
                    {fullText}
                  </p>
                  {isLong && (
                    <button
                      onClick={() => {
                        setTextModalData({ title: "Category", text: fullText });
                        setShowTextModal(true);
                      }}
                      className="absolute bottom-0 right-0 bg-[#0f0f23] pl-2 text-[#C99F4A] hover:text-[#D19B15] text-xs"
                    >
                      …View more
                    </button>
                  )}
                </div>
              );
            })()}
            {isSupremeCourtCase && caseData?.payload?.statusStage && (() => {
              const fullText = caseData.payload.statusStage;
              const isLong = String(fullText).length > 70;
              return (
                <div className="lg:col-span-1 relative">
                  <p
                    className="text-white break-words overflow-hidden"
                    style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                  >
                    <span className="text-gray-400 text-sm">Stage: </span>
                    {fullText}
                  </p>
                  {isLong && (
                    <button
                      onClick={() => {
                        setTextModalData({ title: "Stage", text: fullText });
                        setShowTextModal(true);
                      }}
                      className="absolute bottom-0 right-0 bg-[#0f0f23] pl-2 text-[#C99F4A] hover:text-[#D19B15] text-xs"
                    >
                      …View more
                    </button>
                  )}
                </div>
              );
            })()}
            {/* NGT-specific case info — only shown for cases exported from NGT */}
            {isNgtCase && (ngtInfo.act || caseData?.caseType) && (
              <div>
                <span className="text-gray-400 text-sm">Act: </span>
                <span className="text-white break-words">{ngtInfo.act || caseData?.caseType || "N/A"}</span>
              </div>
            )}
            {isNgtCase && (ngtInfo.lastListed || caseData?.previousHearingDate) && (
              <div>
                <span className="text-gray-400 text-sm">Last Listed: </span>
                <span className="text-white">{ngtInfo.lastListed || caseData?.previousHearingDate || "N/A"}</span>
              </div>
            )}
            {isNgtCase && ngtInfo.zonalBench && (
              <div>
                <span className="text-gray-400 text-sm">Zonal Bench: </span>
                <span className="text-white break-words">{ngtInfo.zonalBench}</span>
              </div>
            )}
            {isNgtCase && (ngtInfo.diaryNumber || ngtInfo.filingNumber || caseData?.filingNumber) && (
              <div>
                <span className="text-gray-400 text-sm">Diary / Filing No: </span>
                <span className="text-white break-words">
                  {ngtInfo.diaryNumber || ngtInfo.filingNumber || caseData?.filingNumber}
                </span>
              </div>
            )}
            {isNgtCase && ngtInfo.registeredOn && (
              <div>
                <span className="text-gray-400 text-sm">Registered On: </span>
                <span className="text-white">{ngtInfo.registeredOn}</span>
              </div>
            )}
            {/* Consumer Court (e-Jagriti) specific fields */}
            {isConsumerCourtCase && (caseData?.cino || consumerInfo.referenceNumber) && (
              <div>
                <span className="text-gray-400 text-sm">Filing Reference Number: </span>
                <span className="text-white break-words">{caseData?.cino || consumerInfo.referenceNumber}</span>
              </div>
            )}
            {isConsumerCourtCase && consumerInfo.caseStage && (
              <div>
                <span className="text-gray-400 text-sm">Case Stage: </span>
                <span className="text-white break-words">{consumerInfo.caseStage}</span>
              </div>
            )}
            {isConsumerCourtCase && consumerInfo.filedIn && (
              <div>
                <span className="text-gray-400 text-sm">Filed In: </span>
                <span className="text-white break-words">{consumerInfo.filedIn}</span>
              </div>
            )}
            {isConsumerCourtCase && consumerInfo.filedBy && consumerInfo.filedBy !== "N/A" && (
              <div>
                <span className="text-gray-400 text-sm">Filed By: </span>
                <span className="text-white break-words">{consumerInfo.filedBy}</span>
              </div>
            )}
            {isConsumerCourtCase && consumerInfo.purpose && consumerInfo.purpose !== "N/A" && (
              <div>
                <span className="text-gray-400 text-sm">Purpose: </span>
                <span className="text-white break-words">{consumerInfo.purpose}</span>
              </div>
            )}
            {isConsumerCourtCase && Array.isArray(consumerInfo.applicationFiled) && consumerInfo.applicationFiled.length > 0 && (
              <div>
                <span className="text-gray-400 text-sm">Applications Filed: </span>
                <span className="text-white break-words">{consumerInfo.applicationFiled.join(", ")}</span>
              </div>
            )}
            {caseData?.authorityDepartment && (
              <div>
                <span className="text-gray-400 text-sm">Authority Department: </span>
                <span className="text-white">
                  {caseData.authorityDepartment === "Others"
                    ? caseData.authorityDepartmentOther || "Others"
                    : caseData.authorityDepartment}
                </span>
              </div>
            )}
            {caseData?.properties && (
              <div>
                <span className="text-gray-400 text-sm">Properties: </span>
                <span className="text-white break-words">{caseData.properties}</span>
              </div>
            )}
            {caseData?.area && (
              <div>
                <span className="text-gray-400 text-sm">Area: </span>
                <span className="text-white break-words">{caseData.area}</span>
              </div>
            )}
            {caseData?.village && (
              <div>
                <span className="text-gray-400 text-sm">Village / Division: </span>
                <span className="text-white break-words">{caseData.village}</span>
              </div>
            )}
            {caseData?.stayOrderDate && (
              <div>
                <span className="text-gray-400 text-sm">Stay Order Date: </span>
                <span className="text-white">{caseData.stayOrderDate}</span>
              </div>
            )}
            {caseData?.counterAffidavitStatus && (
              <div className="md:col-span-2 lg:col-span-1">
                <span className="text-gray-400 text-sm">Counter Affidavit Status: </span>
                <span className="text-white break-words">{caseData.counterAffidavitStatus}</span>
              </div>
            )}
            {caseData?.counterAppealFiled && (
              <div>
                <span className="text-gray-400 text-sm">Counter Appeal Filed: </span>
                <span className="text-white">{caseData.counterAppealFiled}</span>
              </div>
            )}
            {caseData?.prayers && (
              <div className="col-span-full">
                <span className="text-gray-400 text-sm">Prayers: </span>
                <span className="text-white whitespace-pre-wrap break-words">{caseData.prayers}</span>
              </div>
            )}
            <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mt-2">
              <div className="space-y-4">
                <div>
                  <span className="text-gray-400 text-sm">Petitioner Name: </span>
                  <span className="text-white break-words">
                    {caseData.petitionerName || "N/A"}
                    {caseData.petitionerRelation && caseData.petitionerRelationName && (
                      <span className="text-gray-300"> {caseData.petitionerRelation} {caseData.petitionerRelationName}</span>
                    )}
                  </span>
                </div>
                {getOtherPetitioners() && (
                  <div>
                    {renderFieldWithInfo("Other Petitioners", getOtherPetitioners())}
                  </div>
                )}
                {caseData.petitionerAdvocate && (
                  <div>
                    <span className="text-gray-400 text-sm">Petitioner Advocate: </span>
                    <span className="text-white break-words">{caseData.petitionerAdvocate}</span>
                  </div>
                )}
                {getOtherPetitionerAdvocates() && (
                  <div>
                    {renderFieldWithInfo("Other Petitioner Advocates", getOtherPetitionerAdvocates())}
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <span className="text-gray-400 text-sm">Respondent Name: </span>
                  <span className="text-white break-words">
                    {caseData.respondentName || "N/A"}
                    {caseData.respondentRelation && caseData.respondentRelationName && (
                      <span className="text-gray-300"> {caseData.respondentRelation} {caseData.respondentRelationName}</span>
                    )}
                  </span>
                </div>
                {getOtherRespondents() && (
                  <div>
                    {renderFieldWithInfo("Other Respondents", getOtherRespondents())}
                  </div>
                )}
                {caseData.respondentAdvocate && (
                  <div>
                    <span className="text-gray-400 text-sm">Respondent Advocate: </span>
                    <span className="text-white break-words">{caseData.respondentAdvocate}</span>
                  </div>
                )}
                {getOtherRespondentAdvocates() && (
                  <div>
                    {renderFieldWithInfo("Other Respondent Advocates", getOtherRespondentAdvocates())}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
          {/* ===== END OLD GRID LAYOUT ===== */}

          <div className="mb-6">
            <div className="relative border-b border-[#E5E9F2]">
              {tabScroll.left && (
                <>
                  <button
                    type="button"
                    aria-label="Scroll tabs left"
                    onClick={() => scrollTabs(-1)}
                    className="absolute left-0 top-0 bottom-0 z-10 flex items-center justify-center w-9 bg-gradient-to-r from-[#F5F7FA] via-[#F5F7FA]/90 to-transparent text-gray-500 hover:text-[#D19B15]"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                </>
              )}
              {tabScroll.right && (
                <>
                  <button
                    type="button"
                    aria-label="Scroll tabs right"
                    onClick={() => scrollTabs(1)}
                    className="absolute right-0 top-0 bottom-0 z-10 flex items-center justify-center w-9 bg-gradient-to-l from-[#F5F7FA] via-[#F5F7FA]/90 to-transparent text-gray-500 hover:text-[#D19B15]"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              <div
                ref={tabsScrollRef}
                className="cd-tabs-row flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
              {[
                { key: "overview", label: "Overview", Icon: LayoutGrid },
                { key: "timeline", label: "Timeline", Icon: History },
                { key: "case-history", label: "Hearings", Icon: CalendarDays },
                reraOrderHistory && { key: "order-history", label: "Order History", Icon: Gavel },
                hasHighCourtOrders && { key: "hc-orders", label: "Orders", Icon: Gavel },
                isDistrictCourtCase && caseData?.orders?.length > 0 && { key: "dc-orders", label: "Orders", Icon: Gavel },
                hasNgtOrders && { key: "ngt-orders", label: "Orders", Icon: Gavel },
                isConsumerCourtCase && consumerHistory.length > 0 && { key: "consumer-orders", label: "History & Orders", Icon: Gavel },
                isSupremeCourtCase && sciInterlocutory.length > 0 && { key: "sci-ia", label: `Interlocutory Applications (${sciInterlocutory.length})`, Icon: FileText },
                isSupremeCourtCase && sciNotices.length > 0 && { key: "sci-notices", label: `Notices (${sciNotices.length})`, Icon: Bell },
                isSupremeCourtCase && sciEarlierCourts.length > 0 && { key: "sci-earlier-courts", label: `Earlier Court Details (${sciEarlierCourts.length})`, Icon: Landmark },
                isSupremeCourtCase && sciOrders.length > 0 && { key: "sci-orders", label: `Judgements / Orders (${sciOrders.length})`, Icon: Gavel },
                isNcltCase && ncltProceedings.length > 0 && { key: "nclt-order-history", label: "Order History", Icon: Gavel },
                isNcltCase && ncltApplicationDetails.length > 0 && { key: "nclt-application-details", label: "Application Details", Icon: FileText },
                isNcltCase && ncltMainCaseDetails.length > 0 && { key: "nclt-main-details", label: "Main Case Details", Icon: Info },
                isUpReraCase && hasUpReraExtras && { key: "up-rera-docs", label: "Documents & Grounds", Icon: FolderOpen },
                isUpReraCase && upReraInfo.noticeUrl && { key: "up-rera-notices", label: "Notices", Icon: Bell },
                { key: "case-docs", label: "Documents", Icon: FileText, count: linked.docs.length },
                { key: "expenses", label: "Expenses", Icon: Wallet, count: (caseData?.expenses || []).length },
                !isClient && { key: "case-invoices", label: "Invoices", Icon: Receipt, count: linked.invoices.length },
                !isClient && { key: "case-tasks", label: "Tasks", Icon: ListTodo, count: linked.tasks.length },
                caseCollection !== "disposed" && { key: "allow-access", label: "Allow Access", Icon: Lock },
                { key: "case-team", label: "Team", Icon: Users },
                { key: "comments", label: "Comments", Icon: MessageSquare },
                (caseData?.team || []).length > 1 && { key: "attachments", label: "Additional Attachments", Icon: Paperclip },
              ]
                .filter(Boolean)
                .map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={`px-3 py-2.5 text-[13px] font-semibold transition-colors relative whitespace-nowrap flex items-center gap-1.5 ${
                      activeTab === t.key ? "text-[#D19B15]" : "text-gray-400 hover:text-gray-500"
                    }`}
                  >
                    <t.Icon size={14} className="cd-tab-ic" />
                    {t.label}
                    {t.count > 0 && <span className="cd-tab-count">{t.count}</span>}
                    {activeTab === t.key && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D19B15]"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 cd-tab-content">
              {activeTab === "case-docs" && (
                <CaseDocumentsTab
                  caseId={id}
                  caseNo={caseData?.caseNumber || caseData?.caseNo || ""}
                  caseCollection={caseCollection}
                  docs={linked.docs}
                  uploaderUid={globalUser?.uid}
                  onUploaded={(newDocs) =>
                    setLinked((prev) => ({ ...prev, docs: [...newDocs, ...prev.docs] }))
                  }
                />
              )}
              {activeTab === "case-team" && (
                <CaseTeamTab
                  caseData={caseData}
                  onManageAccess={
                    caseCollection !== "disposed" ? () => openTab("allow-access") : null
                  }
                />
              )}
              {activeTab === "case-invoices" && !isClient && (
                <CaseInvoicesTab caseId={id} invoices={linked.invoices} />
              )}
              {activeTab === "case-tasks" && !isClient && (
                <CaseTasksTab caseId={id} tasks={linked.tasks} />
              )}
              {activeTab === "overview" && (
                <>
          {/* ===== Overview top cards (mockup): Hearing Summary | Important Documents | Tasks ===== */}
          <div className="cd-ov3">
            {/* Hearing / order summary */}
            <div className="cd-panel cd-hero-panel">
              <div className="cd-panel-head">
                <h3 className="cd-panel-title">
                  <FileText size={14} className="cd-title-ic ic-amber" />
                  Hearing Summary
                </h3>
                <button className="cd-rail-link" style={{ marginTop: 0 }} onClick={() => openTab("case-history")}>
                  View History
                </button>
              </div>
              {/* ── AI summary of the latest court order (auto-triggers) ── */}
              {(() => {
                if (isReraCase && reraOrderHistory?.rows?.length) {
                  const normalizedOrders = reraOrderHistory.rows.map((row) => {
                    let orderLink = null;
                    for (const header of reraOrderHistory.headers || []) {
                      if (row[header]?.type === "view-order") { orderLink = row[header].url; break; }
                    }
                    return { ...row, url: orderLink };
                  }).filter(o => o.url);
                  if (normalizedOrders.length > 0) return <LatestOrderSummaryCard orders={normalizedOrders} courtType="rera" caseNumber={caseData?.caseNumber || id} />;
                  return null;
                }
                if (isNcltCase && ncltProceedings.filter(p => p.encPath).length > 0) {
                  const normalizedOrders = ncltProceedings.filter(p => p.encPath).map(p => ({ ...p, url: `${NCLT_API_BASE_URL}/order?encPath=${encodeURIComponent(p.encPath)}` }));
                  return <LatestOrderSummaryCard orders={normalizedOrders} courtType="nclt" caseNumber={caseData?.caseNumber || id} />;
                }
                if (isSupremeCourtCase && sciOrders.length > 0) {
                  const normalizedOrders = sciOrders.map(o => ({ ...o, url: o.url || o.documentUrl || "" }));
                  return <LatestOrderSummaryCard orders={normalizedOrders} courtType="sc" caseNumber={caseData?.diaryNumber || caseData?.caseNumber || id} />;
                }
                if (isDistrictCourtCase && caseData?.orders?.length > 0) {
                  return <LatestOrderSummaryCard orders={caseData.orders} courtType="dc" caseNumber={caseData?.caseNumber || id} />;
                }
                if (hasHighCourtOrders) {
                  return <LatestOrderSummaryCard orders={caseData.orders} courtType="hc" caseNumber={caseData?.caseNumber || id} />;
                }
                return null;
              })()}
              <button className="cd-panel-footer-btn" onClick={() => setShowEventsModal(true)}>
                View Hearings & Events <ChevronRight size={14} />
              </button>
            </div>

            {/* Important documents */}
            <div className="cd-panel cd-hero-panel">
              <div className="cd-panel-head">
                <h3 className="cd-panel-title">
                  <FolderOpen size={14} className="cd-title-ic ic-rose" />
                  Important Documents
                </h3>
                <button
                  className="cd-rail-link"
                  style={{ marginTop: 0 }}
                  onClick={() =>
                    navigate(`/portal/case/${id}/case-files${fromPage ? `?from=${fromPage}` : ""}`)
                  }
                >
                  View All Documents
                </button>
              </div>
              {linked.docs.length === 0 ? (
                <p className="cd-rail-muted">No documents uploaded yet</p>
              ) : (
                <div className="cd-ov-list">
                  {linked.docs.slice(0, 4).map((f, i) => (
                    <a
                      key={f.id || i}
                      className="cd-ov-item"
                      href={f.file_url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="cd-ov-item-icon" style={{ background: "#FEF2F2", color: "#DC2626" }}>
                        <FileText size={13} />
                      </span>
                      <span className="cd-ov-item-name" title={f.fileName}>
                        {f.fileName || "Document"}
                      </span>
                      <span className="cd-ov-item-meta">
                        <Download size={13} />
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Tasks */}
            <div className="cd-panel cd-hero-panel">
              <div className="cd-panel-head">
                <h3 className="cd-panel-title">
                  <ListTodo size={14} className="cd-title-ic ic-green" />
                  Tasks
                </h3>
                <button className="cd-rail-link" style={{ marginTop: 0 }} onClick={() => openTab("case-tasks")}>
                  View All Tasks
                </button>
              </div>
              {linked.tasks.length === 0 ? (
                <p className="cd-rail-muted">No tasks linked to this case</p>
              ) : (
                <div className="cd-ov-list" style={{ marginBottom: 10 }}>
                  {linked.tasks.slice(0, 3).map((t) => {
                    const pr = (t.priority || "").toLowerCase();
                    const prStyle =
                      pr === "high"
                        ? { background: "#FEE2E2", color: "#E11D48" }
                        : pr === "low"
                        ? { background: "#DCFCE7", color: "#15803D" }
                        : { background: "#FEF3C7", color: "#B45309" };
                    return (
                      <button
                        key={t.id}
                        className="cd-ov-item"
                        onClick={() => navigate(`/portal/task/${t.id}`)}
                      >
                        <span className="cd-ov-item-icon" style={{ background: "#F5F3FF", color: "#7C3AED" }}>
                          <ListTodo size={13} />
                        </span>
                        <span className="cd-ov-item-name" title={t.title}>
                          {t.title}
                          {t.dueDate && <span className="cd-ov-item-sub">Due on {t.dueDate}</span>}
                        </span>
                        {t.priority && (
                          <span
                            className="cd-ov-pill"
                            style={{ ...prStyle, padding: "3px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 600 }}
                          >
                            {t.priority}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {!isClient && (
                <button
                  className="cd-panel-footer-btn"
                  onClick={() => navigate(`/portal/add-task?caseId=${id}`)}
                >
                  <Plus size={14} /> Add New Task
                </button>
              )}
            </div>
          </div>
          {/* Full Case Information / parties / About now open in the
              "View Full Details" modal (renderFullCaseInfo). */}
                </>
              )}
              {activeTab === "case-history" && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  {/* Consumer Court (e-Jagriti) — hearing timeline */}
                  {isConsumerCourtCase ? (
                    <div>
                      <h3 className="text-[#C99F4A] font-semibold text-lg mb-4">Case History</h3>
                      <ConsumerTimeline timeline={consumerTimeline} initialCount={5} />
                    </div>
                  ) : (isReraCase || isReraHaryanaCase) && reraHistory ? (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[#C99F4A] font-semibold text-lg">RERA Case History</h3>
                        {reraHistory.rows?.length > MAX_VISIBLE_ROWS && (
                          <button
                            onClick={() => {
                              setReraViewAllModalData({ title: "RERA Case History", data: reraHistory });
                              setShowReraViewAllModal(true);
                            }}
                            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                          >
                            View All ({reraHistory.rows.length})
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              {reraHistory.headers?.map((header, idx) => (
                                <th key={idx} className="px-4 py-3 text-left text-gray-300 font-medium">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {reraHistory.rows?.slice(0, MAX_VISIBLE_ROWS).map((row, rowIdx) => (
                              <tr key={rowIdx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                                {reraHistory.headers?.map((header, cellIdx) => (
                                  <td key={cellIdx} className="px-4 py-3 text-white">
                                    {row[header]?.type === "hearing" ? (
                                      <button
                                        onClick={() => handleViewHearing(row[header])}
                                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                      >
                                        View
                                      </button>
                                    ) : row[header]?.type === "order" ? (
                                      <a
                                        href={row[header].downloadLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-blue-300 underline"
                                      >
                                        Download
                                      </a>
                                    ) : (
                                      row[header] || "—"
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {(!reraHistory.rows || reraHistory.rows.length === 0) && (
                        <p className="text-gray-400 text-center py-4">No case history available</p>
                      )}
                    </div>
                  ) : isNcltCase && ncltProceedings.length > 0 ? (
                    /* Show NCLT proceeding details as case history */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[#C99F4A] font-semibold text-lg">Case History</h3>
                        {ncltProceedings.length > MAX_VISIBLE_ROWS && (
                          <button
                            onClick={() => setShowCaseHistoryModal(true)}
                            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                          >
                            View All ({ncltProceedings.length})
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">S. No</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Bench</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Court No</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Listing Date</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Purpose</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Action Taken</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Next Date</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ncltProceedings.slice(0, MAX_VISIBLE_ROWS).map((p, index) => (
                              <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                                <td className="px-4 py-3 text-white">{p.sno || index + 1}</td>
                                <td className="px-4 py-3 text-white">{p.bench_location || "—"}</td>
                                <td className="px-4 py-3 text-white">{p.court_no || "—"}</td>
                                <td className="px-4 py-3 text-white">{p.listing_date || "—"}</td>
                                <td className="px-4 py-3 text-white">{p.listing_purpose || "—"}</td>
                                <td className="px-4 py-3 text-white">{p.action_taken || "—"}</td>
                                <td className="px-4 py-3 text-white">{p.next_list_or_dispose_date || "—"}</td>
                                <td className="px-4 py-3">
                                  <span className={p.status?.toLowerCase() === "dispose" ? "text-red-400 font-semibold" : "text-green-400 font-semibold"}>
                                    {p.status || "—"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : isDistrictCourtCase && caseData.caseHistory?.length > 0 ? (
                    /* Show District Court case history */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[#C99F4A] font-semibold text-lg">District Court Case History</h3>
                        {caseData.caseHistory?.length > MAX_VISIBLE_ROWS && (
                          <button
                            onClick={() => setShowCaseHistoryModal(true)}
                            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                          >
                            View All ({caseData.caseHistory.length})
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Judge</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Business On Date</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Hearing Date</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Purpose of Hearing</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caseData.caseHistory.slice(0, MAX_VISIBLE_ROWS).map((history, index) => (
                              <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                                <td className="px-4 py-3 text-white">{history.judge || "—"}</td>
                                <td className="px-4 py-3">
                                  {history.business_params ? (
                                    <button
                                      onClick={() => handleDcBusinessClick(history)}
                                      className="text-[#C99F4A] hover:text-[#D19B15] underline cursor-pointer"
                                    >
                                      {formatDate(history.business_on_date) || "—"}
                                    </button>
                                  ) : (
                                    <span className="text-white">{formatDate(history.business_on_date) || "—"}</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-white">{formatDate(history.hearing_date) || "—"}</td>
                                <td className="px-4 py-3 text-white">{history.purpose_of_hearing || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : isNgtCase ? (
                    /* Show NGT case history — mirrors the listingHistory table from the fetch flow */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[#C99F4A] font-semibold text-lg">NGT Case History</h3>
                        {caseData.caseHistory?.length > MAX_VISIBLE_ROWS && (
                          <button
                            onClick={() => setShowCaseHistoryModal(true)}
                            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                          >
                            View All ({caseData.caseHistory.length})
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">S. No</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Date of Listing</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Date of Upload</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Coram</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caseData.caseHistory?.length > 0 ? (
                              caseData.caseHistory.slice(0, MAX_VISIBLE_ROWS).map((h, index) => (
                                <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                                  <td className="px-4 py-3 text-white">{index + 1}</td>
                                  <td className="px-4 py-3 text-white">{h.hearingDate || "N/A"}</td>
                                  <td className="px-4 py-3 text-white">{h.businessOnDate || "N/A"}</td>
                                  <td className="px-4 py-3 text-white">{h.judge || "N/A"}</td>
                                  <td className="px-4 py-3">
                                    <button
                                      onClick={() => h.pdfPath && window.open(h.pdfPath, "_blank", "noopener,noreferrer")}
                                      disabled={!h.pdfPath}
                                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="px-4 py-3 text-center text-gray-400">No case history available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : isSupremeCourtCase && caseData.caseHistory?.length > 0 ? (
                    /* Show Supreme Court listing / hearing history (same columns
                       as the search-results details view). */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[#C99F4A] font-semibold text-lg">Listing / Hearing History</h3>
                        {caseData.caseHistory?.length > MAX_VISIBLE_ROWS && (
                          <button
                            onClick={() => setShowCaseHistoryModal(true)}
                            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                          >
                            View All ({caseData.caseHistory.length})
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Date</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Stage</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Purpose</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Judges</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Remarks</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Listed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caseData.caseHistory.slice(0, MAX_VISIBLE_ROWS).map((h, index) => (
                              <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                                <td className="px-4 py-3 text-white">{h.clDate || "—"}</td>
                                <td className="px-4 py-3 text-white">{h.stage || "—"}</td>
                                <td className="px-4 py-3 text-white">{h.purpose || "—"}</td>
                                <td className="px-4 py-3 text-white">{sciList(h.judges).join(", ") || "—"}</td>
                                <td className="px-4 py-3 text-white">{h.remarks || "—"}</td>
                                <td className="px-4 py-3 text-white">{h.listedStatus || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    /* Show regular case history for non-RERA, non-NCLT, non-District Court cases */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[#C99F4A] font-semibold text-lg">Case History</h3>
                        {caseData.caseHistory?.length > MAX_VISIBLE_ROWS && (
                          <button
                            onClick={() => setShowCaseHistoryModal(true)}
                            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                          >
                            View All ({caseData.caseHistory.length})
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Cause List Type</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Judge</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Business On Date</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Hearing Date</th>
                              <th className="px-4 py-3 text-left text-gray-300 font-medium">Purpose</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caseData.caseHistory?.length > 0 ? (
                              caseData.caseHistory.slice(0, MAX_VISIBLE_ROWS).map((history, index) => (
                                <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                                  <td className="px-4 py-3 text-white">{history.causeListType || history.purpose_of_hearing || "N/A"}</td>
                                  <td className="px-4 py-3 text-white">{history.judge || "N/A"}</td>
                                  <td className="px-4 py-3 text-white">{history.businessOnDate || history?.business_on_date || "N/A"}</td>
                                  <td className="px-4 py-3 text-white">{history.hearingDate || history?.hearing_date || "N/A"}</td>
                                  <td className="px-4 py-3 text-white">{history.purpose || history?.purpose_of_hearing || "N/A"}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="px-4 py-3 text-center text-gray-400">No case history available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeTab === "comments" && (
                <CaseCommentsTab
                  caseId={id}
                  caseCollection={caseCollection}
                  uid={globalUser?.uid}
                  userName={globalData?.name || globalData?.displayName || "Unknown"}
                />
              )}
              {activeTab === "attachments" && (
                <CaseAttachmentsTab
                  caseId={id}
                  caseCollection={caseCollection}
                  uid={globalUser?.uid}
                  userName={globalData?.name || globalData?.displayName || "Unknown"}
                />
              )}
              {/* {activeTab === "allow-access" && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <AllowAccess caseData={caseData} caseId={id} onAccessChange={fetchCaseDetails} caseCollection={caseCollection} />
                </div>
              )} */}
              {caseCollection !== "disposed" && activeTab === "allow-access" && (
  <div className="bg-[#16213e] rounded-xl p-6">
    <AllowAccess caseData={caseData} caseId={id} onAccessChange={fetchCaseDetails} caseCollection={caseCollection} />
  </div>
)}
              {activeTab === "expenses" && (
  <div className="bg-[#16213e] rounded-xl p-6">
    <ExpensesTab caseData={caseData} caseId={id} />
  </div>
)}
              {activeTab === "timeline" && (
  <div className="bg-[#16213e] rounded-xl p-6">
    <CaseTimeline caseId={id} caseCollection={caseCollection} caseData={caseData} maxVisible={3} isClientUser={localStorage.getItem("userType") === "client"} />
  </div>
)}
              {/* Order History Tab Content - Only for RERA cases */}
              {activeTab === "order-history" && reraOrderHistory && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">RERA Order History</h3>
                    {reraOrderHistory.rows?.length > MAX_VISIBLE_ROWS && (
                      <button
                        onClick={() => {
                          setReraViewAllModalData({ title: "RERA Order History", data: reraOrderHistory });
                          setShowReraViewAllModal(true);
                        }}
                        className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                      >
                        View All ({reraOrderHistory.rows.length})
                      </button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          {reraOrderHistory.headers?.map((header, idx) => (
                            <th key={idx} className="px-4 py-3 text-left text-gray-300 font-medium">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reraOrderHistory.rows?.slice(0, MAX_VISIBLE_ROWS).map((row, rowIdx) => (
                          <tr key={rowIdx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            {reraOrderHistory.headers?.map((header, cellIdx) => (
                              <td key={cellIdx} className="px-4 py-3 text-white">
                                {row[header]?.type === "view-order" ? (
                                  <div className="flex items-center gap-2">
                                    <a
                                      href={row[header].url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                    >
                                      View
                                    </a>
                                    <button
                                      onClick={() => handleAiSummary({ ...row, url: row[header].url }, "rera")}
                                      className="text-purple-500 hover:text-purple-600 transition-colors"
                                      title="AI Summary"
                                    >
                                      <Sparkles size={16} />
                                    </button>
                                  </div>
                                ) : row[header]?.type === "order" ? (
                                  <a
                                    href={row[header].downloadLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-400 hover:text-green-300 underline"
                                  >
                                    Download
                                  </a>
                                ) : (
                                  row[header] || "—"
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(!reraOrderHistory.rows || reraOrderHistory.rows.length === 0) && (
                    <p className="text-gray-400 text-center py-4">No orders found</p>
                  )}
                </div>
              )}
              {/* High Court Orders Tab Content */}
              {activeTab === "hc-orders" && hasHighCourtOrders && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Orders</h3>
                    <div className="flex items-center gap-4">
                      {caseData.orders.length > MAX_VISIBLE_ROWS ? (
                        <button
                          onClick={() => setShowHCOrdersModal(true)}
                          className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                        >
                          View All ({caseData.orders.length})
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">
                          Total: {caseData.orders.length} order{caseData.orders.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      <button
                        onClick={handleRefreshHighCourtOrders}
                        disabled={isRefreshingOrders}
                        title="Refresh court orders from the High Court portal"
                        className="flex items-center gap-1.5 text-[#C99F4A] hover:text-[#D19B15] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        <RefreshCw size={16} className={isRefreshingOrders ? "animate-spin" : ""} />
                        {isRefreshingOrders ? "Refreshing…" : "Refresh"}
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Number</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order On</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Judge</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {caseData.orders.slice(0, MAX_VISIBLE_ROWS).map((order, idx) => (
                          <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            <td className="px-4 py-3 text-white">{order.orderNumber || "—"}</td>
                            <td className="px-4 py-3 text-white">{order.orderOn || "—"}</td>
                            <td className="px-4 py-3 text-white">{order.judge || "—"}</td>
                            <td className="px-4 py-3 text-white">{order.orderDate || "—"}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {(order.orderLink || order.pdfProxyUrl || order.pdfStorageUrl) ? (
                                  <button
                                    onClick={() => handleViewHighCourtOrder(order.orderLink, order.pdfProxyUrl, order.pdfStorageUrl)}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  >
                                    View
                                  </button>
                                ) : (
                                  <span className="text-gray-500">—</span>
                                )}
                                <button
                                  onClick={() => handleAiSummary(order, "hc")}
                                  className="text-purple-500 hover:text-purple-600 transition-colors"
                                  title="AI Summary"
                                >
                                  <Sparkles size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {caseData.orders.length === 0 && (
                    <p className="text-gray-400 text-center py-4">No orders found</p>
                  )}
                </div>
              )}
              {/* NGT Orders Tab Content */}
              {activeTab === "ngt-orders" && hasNgtOrders && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Orders</h3>
                    <span className="text-gray-400 text-sm">
                      Total: {ngtOrders.length} order{ngtOrders.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">S.No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Type</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ngtOrders.map((order, idx) => (
                          <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            <td className="px-4 py-3 text-white">{idx + 1}</td>
                            <td className="px-4 py-3 text-white">{order.orderDate || "—"}</td>
                            <td className="px-4 py-3 text-white capitalize">{order.type || "order"}</td>
                            <td className="px-4 py-3">
                              {order.url ? (
                                <button
                                  onClick={() => window.open(order.url, "_blank", "noopener,noreferrer")}
                                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                >
                                  View
                                </button>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* History & Orders Tab Content - Consumer Court (e-Jagriti) */}
              {activeTab === "consumer-orders" && isConsumerCourtCase && consumerHistory.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">History &amp; Orders</h3>
                    <span className="text-gray-400 text-sm">Total: {consumerHistory.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-3 py-3 text-left text-gray-300 font-medium">No.</th>
                          <th className="px-3 py-3 text-left text-gray-300 font-medium">Date of Hearing</th>
                          <th className="px-3 py-3 text-left text-gray-300 font-medium">Case Stage</th>
                          <th className="px-3 py-3 text-left text-gray-300 font-medium">Date of Next Hearing</th>
                          <th className="px-3 py-3 text-left text-gray-300 font-medium">Courtroom Proceedings</th>
                          <th className="px-3 py-3 text-left text-gray-300 font-medium">Daily Order</th>
                          <th className="px-3 py-3 text-left text-gray-300 font-medium">Judgement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(showAllConsumerOrders ? consumerHistory : consumerHistory.slice(0, 5)).map((h, idx) => {
                          const viewBtn = (doc) =>
                            doc?.available && doc?.pdfUrl ? (
                              <button
                                onClick={() => window.open(orderPdfProxyUrl(doc.pdfUrl), "_blank", "noopener,noreferrer")}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                View
                              </button>
                            ) : (
                              <span className="text-gray-500">—</span>
                            );
                          return (
                            <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                              <td className="px-3 py-3 text-white">{h.sequenceNumber ?? idx + 1}</td>
                              <td className="px-3 py-3 text-white">{h.dateOfHearing || "—"}</td>
                              <td className="px-3 py-3 text-white">{h.caseStage || "—"}</td>
                              <td className="px-3 py-3 text-white">{h.dateOfNextHearing || "—"}</td>
                              <td className="px-3 py-3">{viewBtn(h.courtroomProceedings)}</td>
                              <td className="px-3 py-3">{viewBtn(h.dailyOrder)}</td>
                              <td className="px-3 py-3">{viewBtn(h.judgement)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {consumerHistory.length > 5 && (
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => setShowAllConsumerOrders((s) => !s)}
                        className="px-5 py-2 rounded-full border border-white/20 text-white/90 hover:bg-white/10 text-sm font-semibold transition-colors"
                      >
                        {showAllConsumerOrders ? "Show Less" : `View All (${consumerHistory.length})`}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* District Court Orders Tab Content */}
              {activeTab === "dc-orders" && isDistrictCourtCase && caseData?.orders?.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Orders</h3>
                    <span className="text-gray-400 text-sm">
                      Total: {caseData.orders.length} order{caseData.orders.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Number</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Details</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {caseData.orders.map((order, idx) => (
                          <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            <td className="px-4 py-3 text-white">{order.order_number || "—"}</td>
                            <td className="px-4 py-3 text-white">{formatDate(order.order_date) || "—"}</td>
                            <td className="px-4 py-3 text-white">{order.order_details || "—"}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {order.pdfProxy ? (
                                  <button
                                    onClick={() => handleDcOrderPdfClick(order, idx)}
                                    disabled={dcOrderLoading === idx}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors disabled:opacity-60 flex items-center gap-1"
                                  >
                                    {dcOrderLoading === idx ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : null}
                                    View
                                  </button>
                                ) : (
                                  <span className="text-gray-500">—</span>
                                )}
                                <button
                                  onClick={() => handleAiSummary(order, "dc")}
                                  className="text-purple-500 hover:text-purple-600 transition-colors"
                                  title="AI Summary"
                                >
                                  <Sparkles size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Supreme Court — Interlocutory Applications */}
              {activeTab === "sci-ia" && isSupremeCourtCase && sciInterlocutory.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Interlocutory Applications</h3>
                    <span className="text-gray-400 text-sm">Total: {sciInterlocutory.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Reg. No.</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Particular</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Filed By</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Filing Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sciInterlocutory.map((ia, idx) => (
                          <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            <td className="px-4 py-3 text-white">{ia.registrationNumber || "—"}</td>
                            <td className="px-4 py-3 text-white">{ia.particular || "—"}</td>
                            <td className="px-4 py-3 text-white">{ia.filedBy || "—"}</td>
                            <td className="px-4 py-3 text-white">{ia.filingDate || "—"}</td>
                            <td className="px-4 py-3 text-white">{ia.status || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Supreme Court — Notices */}
              {activeTab === "sci-notices" && isSupremeCourtCase && sciNotices.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Notices</h3>
                    <span className="text-gray-400 text-sm">Total: {sciNotices.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Type</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Name</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">State</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">District</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Issued</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Returnable</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Dispatched</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sciNotices.map((n, idx) => (
                          <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            <td className="px-4 py-3 text-white">{n.noticeType || "—"}</td>
                            <td className="px-4 py-3 text-white">{n.name || "—"}</td>
                            <td className="px-4 py-3 text-white">{n.state || "—"}</td>
                            <td className="px-4 py-3 text-white">{n.district || "—"}</td>
                            <td className="px-4 py-3 text-white">{n.issueDate || "—"}</td>
                            <td className="px-4 py-3 text-white">{n.returnableDate || "—"}</td>
                            <td className="px-4 py-3 text-white">{n.dispatchDate || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Supreme Court — Earlier Court Details */}
              {activeTab === "sci-earlier-courts" && isSupremeCourtCase && sciEarlierCourts.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Earlier Court Details</h3>
                    <span className="text-gray-400 text-sm">Total: {sciEarlierCourts.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Court</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">State</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Case No.</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Judges</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sciEarlierCourts.map((e, idx) => (
                          <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            <td className="px-4 py-3 text-white">{e.court || "—"}</td>
                            <td className="px-4 py-3 text-white">{e.agencyState || "—"}</td>
                            <td className="px-4 py-3 text-white">{e.caseNumber || "—"}</td>
                            <td className="px-4 py-3 text-white">{e.orderDate || "—"}</td>
                            <td className="px-4 py-3 text-white">{sciList(e.judges).join(", ") || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Supreme Court — Judgements / Orders */}
              {activeTab === "sci-orders" && isSupremeCourtCase && sciOrders.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Judgements / Orders</h3>
                    <span className="text-gray-400 text-sm">Total: {sciOrders.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Document</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sciOrders.map((o, idx) => {
                          const orderUrl = o.url || o.documentUrl || "";
                          return (
                            <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                              <td className="px-4 py-3 text-white">{cleanOrderName(o.documentName || o.description)}</td>
                              <td className="px-4 py-3 text-white">{o.date || "—"}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {orderUrl ? (
                                    <button
                                      onClick={() => window.open(orderUrl, "_blank", "noopener,noreferrer")}
                                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                    >
                                      View
                                    </button>
                                  ) : (
                                    <span className="text-gray-500">—</span>
                                  )}
                                  <button
                                    onClick={() => handleAiSummary({ ...o, url: orderUrl }, "sc")}
                                    className="text-purple-500 hover:text-purple-600 transition-colors"
                                    title="AI Summary"
                                  >
                                    <Sparkles size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* NCLT Order History Tab Content */}
              {activeTab === "nclt-order-history" && isNcltCase && ncltProceedings.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Order History</h3>
                    {ncltProceedings.length > MAX_VISIBLE_ROWS && (
                      <button
                        onClick={() => setShowCaseHistoryModal(true)}
                        className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
                      >
                        View All ({ncltProceedings.length})
                      </button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">S. No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Bench</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Court No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Listing Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order Name</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Upload Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Status</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Order</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ncltProceedings.filter(p => p.encPath).slice(0, MAX_VISIBLE_ROWS).map((p, index) => (
                          <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                            <td className="px-4 py-3 text-white">{index + 1}</td>
                            <td className="px-4 py-3 text-white">{p.bench_location || "—"}</td>
                            <td className="px-4 py-3 text-white">{p.court_no || "—"}</td>
                            <td className="px-4 py-3 text-white">{p.listing_date || "—"}</td>
                            <td className="px-4 py-3 text-white">{p.order_name || "—"}</td>
                            <td className="px-4 py-3 text-white">{p.order_upload_datetime || "—"}</td>
                            <td className="px-4 py-3">
                              <span className={p.status?.toLowerCase() === "dispose" ? "text-red-400 font-semibold" : "text-green-400 font-semibold"}>
                                {p.status || "—"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <a
                                  href={`${NCLT_API_BASE_URL}/order?encPath=${encodeURIComponent(p.encPath)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                >
                                  View
                                </a>
                                <button
                                  onClick={() => handleAiSummary({ ...p, url: `${NCLT_API_BASE_URL}/order?encPath=${encodeURIComponent(p.encPath)}` }, "nclt")}
                                  className="text-purple-500 hover:text-purple-600 transition-colors"
                                  title="AI Summary"
                                >
                                  <Sparkles size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {ncltProceedings.filter(p => p.encPath).length === 0 && (
                    <p className="text-gray-400 text-center py-4">No orders available</p>
                  )}
                </div>
              )}
              {activeTab === "nclt-application-details" && isNcltCase && ncltApplicationDetails.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Application Details (IA / MA)</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1080px] border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">S. No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Filing No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Case Type</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Case Title</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Bench / Court</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Application No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Filing / Reg Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Next / Dispose Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ncltApplicationDetails.map((a, index) => (
                          <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50 align-top">
                            <td className="px-4 py-3 text-white whitespace-nowrap">{a.sno || index + 1}</td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">{a.filing_no || a.filingNo || "—"}</td>
                            <td className="px-4 py-3 text-white min-w-[150px]">{a.case_type || "—"}</td>
                            <td className="px-4 py-3 text-white min-w-[220px]">
                              {(a.case_title1 || a.case_title2) ? (
                                <>
                                  <div>{a.case_title1 || "—"}</div>
                                  {a.case_title2 ? (
                                    <>
                                      <span className="text-[#C99F4A] font-semibold">VS</span>
                                      <div>{a.case_title2}</div>
                                    </>
                                  ) : null}
                                </>
                              ) : "—"}
                            </td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">
                              {a.bench_location || "—"}{a.court_no ? ` / ${a.court_no}` : ""}
                            </td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">{a.application_no || a.caseNumber || a.case_number || "—"}</td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">
                              {a.filing_date || "—"}{a.registration_date ? ` / ${a.registration_date}` : ""}
                            </td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">
                              {(a.next_list_date && a.next_list_date !== "NA") ? a.next_list_date
                                : (a.disposal_date && a.disposal_date !== "NA") ? a.disposal_date
                                : (a.next_list_or_dispose_date || "—")}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={
                                (a.case_status || a.status || "").toLowerCase().includes("dispos")
                                  ? "text-red-400 font-semibold"
                                  : "text-green-400 font-semibold"
                              }>
                                {a.case_status || a.status || "—"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Main / Parent Case Details Tab Content */}
              {activeTab === "nclt-main-details" && isNcltCase && ncltMainCaseDetails.length > 0 && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[#C99F4A] font-semibold text-lg">Main / Parent Case</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1080px] border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">S. No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Filing No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Case Type</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Case Title</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Bench / Court</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Case No</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Filing / Reg Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Next / Dispose Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ncltMainCaseDetails.map((m, index) => (
                          <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50 align-top">
                            <td className="px-4 py-3 text-white whitespace-nowrap">{m.sno || index + 1}</td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">{m.filing_no || "—"}</td>
                            <td className="px-4 py-3 text-white min-w-[150px]">{m.case_type || "—"}</td>
                            <td className="px-4 py-3 text-white min-w-[220px]">
                              {(m.case_title1 || m.case_title2) ? (
                                <>
                                  <div>{m.case_title1 || "—"}</div>
                                  {m.case_title2 ? (
                                    <>
                                      <span className="text-[#C99F4A] font-semibold">VS</span>
                                      <div>{m.case_title2}</div>
                                    </>
                                  ) : null}
                                </>
                              ) : "—"}
                            </td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">
                              {m.bench_location || "—"}{m.court_no ? ` / ${m.court_no}` : ""}
                            </td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">{m.case_no || "—"}</td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">
                              {m.filing_date || "—"}{m.registration_date ? ` / ${m.registration_date}` : ""}
                            </td>
                            <td className="px-4 py-3 text-white whitespace-nowrap">
                              {(m.next_list_date && m.next_list_date !== "NA") ? m.next_list_date
                                : (m.disposal_date && m.disposal_date !== "NA") ? m.disposal_date
                                : "—"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={
                                (m.case_status || "").toLowerCase().includes("dispos")
                                  ? "text-red-400 font-semibold"
                                  : "text-green-400 font-semibold"
                              }>
                                {m.case_status || "—"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Documents & Grounds Tab - UP RERA */}
              {activeTab === "up-rera-docs" && isUpReraCase && (
                <div className="bg-[#16213e] rounded-xl p-6 space-y-8">
                  {upReraProofs.length > 0 && (
                    <div>
                      <h3 className="text-[#C99F4A] font-semibold text-lg mb-4">Documentary Proofs</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              {Object.keys(upReraProofs[0]).map((h) => (
                                <th key={h} className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {upReraProofs.map((r, i) => (
                              <tr key={i} className="border-t border-gray-700 hover:bg-[#0f0f23]/50 align-top">
                                {Object.keys(upReraProofs[0]).map((h) => (
                                  <td key={h} className="px-4 py-3 text-white break-words">{r[h] || "—"}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {upReraGrounds.length > 0 && (
                    <div>
                      <h3 className="text-[#C99F4A] font-semibold text-lg mb-4">Grounds for Complaint</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#0f0f23]">
                              {Object.keys(upReraGrounds[0]).map((h) => (
                                <th key={h} className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {upReraGrounds.map((r, i) => (
                              <tr key={i} className="border-t border-gray-700 hover:bg-[#0f0f23]/50 align-top">
                                {Object.keys(upReraGrounds[0]).map((h) => (
                                  <td key={h} className="px-4 py-3 text-white break-words">{r[h] || "—"}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Notices Tab - UP RERA */}
              {activeTab === "up-rera-notices" && isUpReraCase && upReraInfo.noticeUrl && (
                <div className="bg-[#16213e] rounded-xl p-6">
                  <h3 className="text-[#C99F4A] font-semibold text-lg mb-4">Notices</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#0f0f23]">
                          <th className="px-4 py-3 text-left text-gray-300 font-medium whitespace-nowrap">Date</th>
                          <th className="px-4 py-3 text-left text-gray-300 font-medium">Notice</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-gray-700 hover:bg-[#0f0f23]/50 align-top">
                          <td className="px-4 py-3 text-white whitespace-nowrap">
                            {caseData?.nextHearingDate || upReraInfo.registrationDate || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <a
                              href={upReraInfo.noticeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 underline hover:text-blue-300"
                            >
                              View Notice
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Notes edit modal (hero "Notes" card) */}
      {showNotesModal && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !savingNotes && setShowNotesModal(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="m-0 text-base font-bold text-slate-900">Edit Notes</h2>
              <button
                className="text-slate-400 transition-colors hover:text-slate-700"
                onClick={() => setShowNotesModal(false)}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={7}
              placeholder="Write case notes…"
              className="w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                onClick={() => setShowNotesModal(false)}
                disabled={savingNotes}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-yellow-600 disabled:opacity-50"
                onClick={handleSaveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? "Saving…" : "Save Notes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Case Progress editor — every step with its status, custom steps in
          between, Disposed fixed at the end. */}
      {showProgressModal && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowProgressModal(false);
            setEditingStepKey(null);
          }}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl max-h-[88vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="m-0 flex items-center gap-2 text-base font-bold text-slate-900">
                Case Progress
                <span
                  className={`cd-status-pill ${statusBucket === "disposed" ? "cd-status-disposed" : "cd-status-pending"}`}
                >
                  {statusLabel(caseData?.status) || "Pending"}
                </span>
              </h2>
              <button
                className="text-slate-400 transition-colors hover:text-slate-700"
                onClick={() => {
                  setShowProgressModal(false);
                  setEditingStepKey(null);
                }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="cd-prog-list">
              {progressSteps.map((s) => (
                <div key={s.key} className={`cd-prog-item ${s.state}`}>
                  <span className="cd-prog-dot">
                    {s.state === "done" && <Check size={11} strokeWidth={3} />}
                  </span>
                  <div className="cd-prog-body">
                    {editingStepKey === s.key ? (
                      <div className="cd-prog-edit-row">
                        <input
                          type="text"
                          value={stepLabelDraft}
                          onChange={(e) => setStepLabelDraft(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && !savingSteps && handleSaveStepEdit()}
                          placeholder="Step name"
                          autoFocus
                        />
                        <DatePicker
                          selected={stepDateDraft}
                          onChange={(date) => setStepDateDraft(date)}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="DD/MM/YYYY"
                          portalId="cd-dp-portal"
                        />
                        <button
                          className="cd-prog-edit-save"
                          disabled={savingSteps}
                          onClick={handleSaveStepEdit}
                          title="Save step"
                        >
                          <Check size={13} strokeWidth={3} />
                        </button>
                        <button
                          disabled={savingSteps}
                          onClick={() => setEditingStepKey(null)}
                          title="Cancel"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="cd-prog-info">
                          <p className="cd-prog-label" title={s.label}>{s.label}</p>
                          <p className="cd-prog-date">
                            {s.dateText || (s.state === "todo" ? "Pending" : "—")}
                          </p>
                        </div>
                        <span className={`cd-prog-badge ${s.state}`}>
                          {s.state === "done" ? "Completed" : s.state === "current" ? "Current" : "Pending"}
                        </span>
                        {!isClient && !isLocked && (
                          <div className="cd-prog-actions">
                            <button
                              disabled={savingSteps}
                              onClick={() => openStepEdit(s)}
                              title="Edit step"
                            >
                              <Pencil size={12} />
                            </button>
                            {s.custom && (
                              <>
                                <button
                                  className={`cd-prog-check ${s.done ? "on" : ""}`}
                                  disabled={savingSteps || statusBucket === "disposed"}
                                  onClick={() => handleToggleCustomStep(s.id)}
                                  title={s.done ? "Mark as pending" : "Mark as completed"}
                                >
                                  <Check size={12} strokeWidth={3} />
                                </button>
                                <button
                                  disabled={savingSteps}
                                  onClick={() => handleMoveCustomStep(s.id, -1)}
                                  title="Move up"
                                >
                                  <ChevronUp size={14} />
                                </button>
                                <button
                                  disabled={savingSteps}
                                  onClick={() => handleMoveCustomStep(s.id, 1)}
                                  title="Move down"
                                >
                                  <ChevronDown size={14} />
                                </button>
                                <button
                                  className="cd-prog-del"
                                  disabled={savingSteps}
                                  onClick={() => handleDeleteCustomStep(s.id)}
                                  title="Remove step"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {s.key === "end" && statusBucket !== "disposed" && !isClient && !isLocked && (
                          <button
                            className="cd-prog-dispose-btn"
                            onClick={() => {
                              setShowProgressModal(false);
                              setShowDisposeModal(true);
                            }}
                          >
                            Dispose Case…
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {!isClient && !isLocked && statusBucket !== "disposed" && (
              <div className="cd-prog-add">
                <div ref={stepStatusRef} className="relative flex-1 min-w-0">
                  <button
                    type="button"
                    title="Step status"
                    onClick={() => setStepStatusOpen((v) => !v)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                  >
                    <span className={`truncate ${newStepLabel ? "" : "text-slate-400"}`}>
                      {newStepLabel || "Select status…"}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`shrink-0 text-slate-400 transition-transform ${stepStatusOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {stepStatusOpen && (
                    <div className="absolute bottom-full left-0 z-30 mb-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
                      {CASE_STATUSES.filter((s) => s.bucket === "pending").map((s) => {
                        const sel = newStepLabel === s.value;
                        return (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => {
                              setNewStepLabel(s.value);
                              setStepStatusOpen(false);
                            }}
                            className={`flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] ${
                              sel
                                ? "bg-yellow-50 font-medium text-slate-900"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            <span className="truncate">{s.value}</span>
                            {sel && <Check size={13} strokeWidth={2.5} className="shrink-0 text-yellow-600" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <DatePicker
                  selected={newStepDate}
                  onChange={(date) => setNewStepDate(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                  popperPlacement="top-start"
                  title="Step date (optional)"
                />
                <button
                  className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-500 px-3.5 py-2 text-sm font-semibold text-gray-900 hover:bg-yellow-600 disabled:opacity-50 whitespace-nowrap"
                  onClick={handleAddCustomStep}
                  disabled={savingSteps}
                >
                  <Plus size={14} strokeWidth={2.5} />
                  {savingSteps ? "Saving…" : "Add Step"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Full case details modal (View Full Details) */}
      {showFullDetailsModal && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowFullDetailsModal(false)}
        >
          <div className="cd-full-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cd-full-modal-head">
              <h2>Case Details</h2>
              <button onClick={() => setShowFullDetailsModal(false)} title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="cd-full-modal-body">{renderFullCaseInfo()}</div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1a1a2e] p-6 rounded-lg">
            <p className="text-white mb-4">Are you sure you want to delete this case?</p>
            <div className="flex justify-between gap-4">
              <button
                onClick={() => {
                  handleDeleteCase();
                  setShowDeleteModal(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Yes
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showNamesModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 p-6 border-b border-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#C99F4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-white">{namesModalData.title}</h3>
              <button
                onClick={() => setShowNamesModal(false)}
                className="ml-auto text-gray-400 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {namesModalData.names.length > 1 ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-400 mb-4">
                    Total: {namesModalData.names.length} {namesModalData.names.length === 1 ? 'person' : 'people'}
                  </div>
                  {namesModalData.names.map((name, index) => (
                    <div
                      key={index}
                      className="bg-[#0f0f23] rounded-lg p-4 flex items-center gap-4 hover:bg-opacity-80 transition-all"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C99F4A] flex items-center justify-center text-[#0f0f23] font-semibold">
                        {index + 1}
                      </div>
                      <div className="text-white text-base flex-1 break-words">
                        {name}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-white text-base">
                  {namesModalData.names[0] || "N/A"}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setShowNamesModal(false)}
                className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Text View All Modal (Category / Stage) */}
      {showTextModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">{textModalData.title}</h3>
              <button
                onClick={() => setShowTextModal(false)}
                className="ml-auto text-gray-400 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="text-white text-base whitespace-pre-wrap break-words">
                {textModalData.text || "N/A"}
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setShowTextModal(false)}
                className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {disposeSharingModal}

      {/* Dispose Confirmation Modal */}
      {showDisposeModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1a1a2e] p-6 rounded-lg border border-gray-700 max-w-md w-full mx-4">
            <h3 className="text-white text-lg font-semibold mb-4">Confirm Disposal</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to dispose this case?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDisposeModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                No
              </button>
              <button
                onClick={handleDisposeConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hearing Date Selection Modal */}
      {showHearingDateModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] p-6 rounded-lg border border-gray-700 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-white text-lg font-semibold mb-4">Select Last Hearing Date</h3>
            <p className="text-gray-300 mb-6">Please select the last hearing date for this case:</p>

            <div className="space-y-3 mb-6">
              {caseData?.previousHearingDate && (
                <button
                  onClick={() => setSelectedHearingDate(caseData.previousHearingDate)}
                  className={`w-full p-4 rounded-lg border text-left transition-colors ${
                    selectedHearingDate === caseData.previousHearingDate
                      ? 'border-[#C99F4A] bg-[#C99F4A]/10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="text-gray-400 text-sm">Previous Hearing Date</div>
                  <div className="text-white font-medium">{caseData.previousHearingDate}</div>
                </button>
              )}

              {(() => {
                const parseDMY = (s) => {
                  if (!s || typeof s !== "string") return null;
                  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  if (!m) return null;
                  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
                  d.setHours(0, 0, 0, 0);
                  return d;
                };
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const dd = String(today.getDate()).padStart(2, "0");
                const mm = String(today.getMonth() + 1).padStart(2, "0");
                const yyyy = today.getFullYear();
                const todayDmy = `${dd}/${mm}/${yyyy}`;
                const nhd = parseDMY(caseData?.nextHearingDate);
                const showAsToday = nhd && nhd > today;
                const label = showAsToday
                  ? "Today"
                  : caseData?.nextHearingDate
                    ? "Next Hearing Date"
                    : null;
                const value = showAsToday ? todayDmy : caseData?.nextHearingDate;
                if (!label || !value) return null;
                return (
                  <button
                    onClick={() => setSelectedHearingDate(value)}
                    className={`w-full p-4 rounded-lg border text-left transition-colors ${
                      selectedHearingDate === value
                        ? 'border-[#C99F4A] bg-[#C99F4A]/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="text-gray-400 text-sm">{label}</div>
                    <div className="text-white font-medium">{value}</div>
                  </button>
                );
              })()}
            </div>

            <div className="border-t border-gray-700 pt-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-medium text-sm">Dispose With Direction?</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDisposeWithDirection(true)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      disposeWithDirection
                        ? 'border-[#C99F4A] bg-[#C99F4A]/15 text-[#C99F4A]'
                        : 'border-gray-600 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDisposeWithDirection(false);
                      setDirectionDate("");
                      setDirectionNotes("");
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      !disposeWithDirection
                        ? 'border-[#C99F4A] bg-[#C99F4A]/15 text-[#C99F4A]'
                        : 'border-gray-600 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {disposeWithDirection && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 uppercase tracking-wide">Tentative Date of Direction (optional)</label>
                    <input
                      type="date"
                      value={directionDate}
                      onChange={(e) => setDirectionDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[#0f0f23] border border-gray-700 text-white text-sm focus:outline-none focus:border-[#C99F4A]"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 uppercase tracking-wide">Timeframe of Direction</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {[
                        { key: "week", label: "Week" },
                        { key: "month", label: "Month" },
                        { key: "year", label: "Year" },
                        { key: "range", label: "Start - End" },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => {
                            setDirectionTimeframeType(opt.key);
                            if (opt.key === "range") {
                              setDirectionTimeframeValue("");
                            } else {
                              setDirectionTimeframeStart("");
                              setDirectionTimeframeEnd("");
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            directionTimeframeType === opt.key
                              ? 'border-[#C99F4A] bg-[#C99F4A]/15 text-[#C99F4A]'
                              : 'border-gray-600 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {directionTimeframeType !== "range" ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={directionTimeframeValue}
                          onChange={(e) => setDirectionTimeframeValue(e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder={`e.g. 2`}
                          className="w-24 px-3 py-2 rounded-lg bg-[#0f0f23] border border-gray-700 text-white text-sm focus:outline-none focus:border-[#C99F4A]"
                        />
                        <span className="text-gray-300 text-sm capitalize">
                          {directionTimeframeType}
                          {directionTimeframeValue && Number(directionTimeframeValue) !== 1 ? "s" : ""}
                        </span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-gray-500 text-[10px] mb-1 uppercase tracking-wide">Start</label>
                          <input
                            type="date"
                            value={directionTimeframeStart}
                            onChange={(e) => setDirectionTimeframeStart(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg bg-[#0f0f23] border border-gray-700 text-white text-sm focus:outline-none focus:border-[#C99F4A]"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-500 text-[10px] mb-1 uppercase tracking-wide">End</label>
                          <input
                            type="date"
                            value={directionTimeframeEnd}
                            min={directionTimeframeStart || undefined}
                            onChange={(e) => setDirectionTimeframeEnd(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg bg-[#0f0f23] border border-gray-700 text-white text-sm focus:outline-none focus:border-[#C99F4A]"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 uppercase tracking-wide">Notes on direction (optional)</label>
                    <textarea
                      value={directionNotes}
                      onChange={(e) => setDirectionNotes(e.target.value)}
                      rows={2}
                      placeholder="Add any notes about the direction"
                      className="w-full px-3 py-2 rounded-lg bg-[#0f0f23] border border-gray-700 text-white text-sm focus:outline-none focus:border-[#C99F4A] resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowHearingDateModal(false);
                  setSelectedHearingDate(null);
                  setDisposeWithDirection(true);
                  setDirectionDate("");
                  setDirectionNotes("");
                  setDirectionTimeframeType("month");
                  setDirectionTimeframeValue("");
                  setDirectionTimeframeStart("");
                  setDirectionTimeframeEnd("");
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDisposeCase(selectedHearingDate)}
                disabled={!selectedHearingDate || isDisposing}
                className={`px-4 py-2 rounded font-medium flex items-center gap-2 ${
                  selectedHearingDate && !isDisposing
                    ? 'bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23]'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isDisposing && <Loader2 className="w-4 h-4 animate-spin" />}
                {isDisposing ? 'Disposing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Hearing Date Modal */}
      {editingHearingField && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1535] text-white p-6 rounded-xl shadow-xl w-full max-w-sm border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-yellow-500">
                {editingHearingField === "next"
                  ? "Change Next Hearing Date"
                  : "Change Previous Hearing Date"}
              </h3>
              <button
                onClick={closeHearingEdit}
                className="text-gray-400 hover:text-white"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-5">
              <label className="block text-sm text-gray-300 mb-2">
                {editingHearingField === "next"
                  ? "Select a new next hearing date"
                  : "Select a new previous hearing date"}
              </label>
              <DatePicker
                selected={editingHearingDate}
                onChange={(date) => setEditingHearingDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                minDate={
                  editingHearingField === "next"
                    ? new Date()
                    : undefined
                }
                maxDate={
                  editingHearingField === "previous" ? new Date() : undefined
                }
                className="w-full bg-[#0A061E] text-white p-3 rounded-lg border border-gray-700 focus:border-yellow-500 focus:ring-yellow-500"
                popperPlacement="bottom-start"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeHearingEdit}
                disabled={isSavingHearingDate}
                className="px-4 py-2 rounded text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveHearingDate}
                disabled={isSavingHearingDate || !editingHearingDate}
                className="px-4 py-2 rounded text-sm font-medium bg-yellow-500 text-black hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSavingHearingDate && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSavingHearingDate ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Default Reminder Info Modal (shown after next hearing date updated) */}
      {showReminderInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1535] rounded-xl border border-gray-700 max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-white text-center mb-4">
                Default Reminders
              </h3>
              <div className="flex flex-col items-center space-y-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-gray-300 text-center leading-relaxed">
                  Case hearing reminder is set at 8 AM on the hearing date and a
                  next hearing date update reminder is set at 6 PM on the same day.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 border-t border-gray-700">
              <button
                onClick={() => setShowReminderInfoModal(false)}
                className="px-8 py-3 bg-white hover:bg-gray-200 text-[#1A1535] font-semibold rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Case Events Modal */}
      <ViewCaseEventsModal
        isOpen={showEventsModal}
        onClose={() => setShowEventsModal(false)}
        caseData={caseData}
        onAddEventClick={() => {
          setShowEventsModal(false);
          setShowAddEventModal(true);
        }}
      />

      {/* Add Case Event Modal */}
      <AddCaseEventModal
        isOpen={showAddEventModal}
        onClose={() => setShowAddEventModal(false)}
        caseData={caseData}
        onEventAdded={() => {
          setShowAddEventModal(false);
          setShowEventsModal(true);
        }}
      />

      {/* Export Case Info Modal */}
      {showExportInfoModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <Info size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-2">Export Case</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Save the case to your library of all cases.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 border-t border-gray-700">
              <button
                onClick={() => setShowExportInfoModal(false)}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RERA View All Modal */}
      {showReraViewAllModal && reraViewAllModalData.data && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-24 overflow-y-auto">
          <div className="bg-[#1a1a2e] rounded-2xl border border-gray-700 max-w-5xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-[#C99F4A] font-semibold text-xl">
                {reraViewAllModalData.title}
              </h2>
              <button
                onClick={() => setShowReraViewAllModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#0f0f23]">
                      {reraViewAllModalData.data.headers?.map((header, idx) => (
                        <th key={idx} className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reraViewAllModalData.data.rows?.map((row, rowIdx) => (
                      <tr key={rowIdx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                        {reraViewAllModalData.data.headers?.map((header, cellIdx) => (
                          <td key={cellIdx} className="px-4 py-3 text-white">
                            {row[header]?.type === "view-order" ? (
                              <a
                                href={row[header].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                View
                              </a>
                            ) : row[header]?.type === "hearing" ? (
                              <button
                                onClick={() => handleViewHearing(row[header])}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                View
                              </button>
                            ) : row[header]?.type === "order" ? (
                              <a
                                href={row[header].downloadLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-400 hover:text-green-300 underline"
                              >
                                Download
                              </a>
                            ) : (
                              row[header] || "—"
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setShowReraViewAllModal(false)}
                className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Case History View All Modal */}
      {showCaseHistoryModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-24 overflow-y-auto">
          <div className="bg-[#1a1a2e] rounded-2xl border border-gray-700 max-w-5xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-[#C99F4A] font-semibold text-xl">Case History</h2>
              <button
                onClick={() => setShowCaseHistoryModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="overflow-x-auto">
                {isNcltCase && ncltProceedings.length > 0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#0f0f23]">
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">S. No</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Bench</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Court No</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Listing Date</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Purpose</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Action Taken</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Next Date</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ncltProceedings.map((p, index) => (
                        <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                          <td className="px-4 py-3 text-white">{p.sno || index + 1}</td>
                          <td className="px-4 py-3 text-white">{p.bench_location || "—"}</td>
                          <td className="px-4 py-3 text-white">{p.court_no || "—"}</td>
                          <td className="px-4 py-3 text-white">{p.listing_date || "—"}</td>
                          <td className="px-4 py-3 text-white">{p.listing_purpose || "—"}</td>
                          <td className="px-4 py-3 text-white">{p.action_taken || "—"}</td>
                          <td className="px-4 py-3 text-white">{p.next_list_or_dispose_date || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={p.status?.toLowerCase() === "dispose" ? "text-red-400 font-semibold" : "text-green-400 font-semibold"}>
                              {p.status || "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : isDistrictCourtCase ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#0f0f23]">
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Judge</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Business On Date</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Hearing Date</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Purpose of Hearing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseData?.caseHistory?.map((history, index) => (
                        <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                          <td className="px-4 py-3 text-white">{history.judge || "—"}</td>
                          <td className="px-4 py-3">
                            {history.business_params ? (
                              <button
                                onClick={() => handleDcBusinessClick(history)}
                                className="text-[#C99F4A] hover:text-[#D19B15] underline cursor-pointer"
                              >
                                {formatDate(history.business_on_date) || "—"}
                              </button>
                            ) : (
                              <span className="text-white">{formatDate(history.business_on_date) || "—"}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-white">{formatDate(history.hearing_date) || "—"}</td>
                          <td className="px-4 py-3 text-white">{history.purpose_of_hearing || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : isSupremeCourtCase ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#0f0f23]">
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Date</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Stage</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Purpose</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Judges</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Remarks</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Listed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseData?.caseHistory?.map((h, index) => (
                        <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                          <td className="px-4 py-3 text-white">{h.clDate || "—"}</td>
                          <td className="px-4 py-3 text-white">{h.stage || "—"}</td>
                          <td className="px-4 py-3 text-white">{h.purpose || "—"}</td>
                          <td className="px-4 py-3 text-white">{sciList(h.judges).join(", ") || "—"}</td>
                          <td className="px-4 py-3 text-white">{h.remarks || "—"}</td>
                          <td className="px-4 py-3 text-white">{h.listedStatus || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#0f0f23]">
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Cause List Type</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Judge</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Business On Date</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Hearing Date</th>
                        <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseData?.caseHistory?.map((history, index) => (
                        <tr key={index} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                          <td className="px-4 py-3 text-white">{history.causeListType || history.purpose_of_hearing || "N/A"}</td>
                          <td className="px-4 py-3 text-white">{history.judge || "N/A"}</td>
                          <td className="px-4 py-3 text-white">{history.businessOnDate || history?.business_on_date || "N/A"}</td>
                          <td className="px-4 py-3 text-white">{history.hearingDate || history?.hearing_date || "N/A"}</td>
                          <td className="px-4 py-3 text-white">{history.purpose || history?.purpose_of_hearing || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setShowCaseHistoryModal(false)}
                className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* High Court Orders View All Modal */}
      {showHCOrdersModal && hasHighCourtOrders && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-24 overflow-y-auto">
          <div className="bg-[#1a1a2e] rounded-2xl border border-gray-700 max-w-5xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-[#C99F4A] font-semibold text-xl">Orders</h2>
              <button
                onClick={() => setShowHCOrdersModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#0f0f23]">
                      <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Order Number</th>
                      <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Order On</th>
                      <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Judge</th>
                      <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Order Date</th>
                      <th className="px-4 py-3 text-left text-gray-300 font-medium sticky top-0 bg-[#0f0f23]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseData?.orders?.map((order, idx) => (
                      <tr key={idx} className="border-t border-gray-700 hover:bg-[#0f0f23]/50">
                        <td className="px-4 py-3 text-white">{order.orderNumber || "—"}</td>
                        <td className="px-4 py-3 text-white">{order.orderOn || "—"}</td>
                        <td className="px-4 py-3 text-white">{order.judge || "—"}</td>
                        <td className="px-4 py-3 text-white">{order.orderDate || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {(order.orderLink || order.pdfProxyUrl) ? (
                              <button
                                onClick={() => handleViewHighCourtOrder(order.orderLink, order.pdfProxyUrl)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                View
                              </button>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                            <button
                              onClick={() => handleAiSummary(order, "hc")}
                              className="text-purple-500 hover:text-purple-600 transition-colors"
                              title="AI Summary"
                            >
                              <Sparkles size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setShowHCOrdersModal(false)}
                className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* District Court Business Detail Modal */}
      {dcBusinessModalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-2xl border border-gray-700 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">Business Detail</h3>
              <div className="flex items-center gap-2">
                {dcBusinessDetail && (
                  <button
                    onClick={handleDcPrintBusiness}
                    className="text-gray-400 hover:text-white p-1"
                    title="Print"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => setDcBusinessModalOpen(false)}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto">
              {dcBusinessLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#C99F4A]" />
                  <span className="ml-3 text-gray-400">Loading business details...</span>
                </div>
              ) : dcBusinessDetail ? (
                <div className="space-y-3">
                  {[
                    ["Court", dcBusinessDetail.court_of],
                    ["CNR Number", dcBusinessDetail.cnr_number],
                    ["Case Number", dcBusinessDetail.case_number],
                    ["Date", dcBusinessDetail.date],
                    ["Parties", dcBusinessDetail.parties],
                    ["Business", dcBusinessDetail.business],
                    ["Next Purpose", dcBusinessDetail.next_purpose],
                    ["Next Hearing Date", dcBusinessDetail.next_hearing_date],
                  ].map(([label, value]) => (
                    <div key={label} className="flex py-1">
                      <span className="w-44 text-sm text-gray-300 shrink-0">{label}:</span>
                      <span className="font-bold flex-1 text-white">{value || "N/A"}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-4">No business details available</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* AI order-summary popups */}
      <AISummaryModal
        isOpen={aiSummaryModal.open}
        onClose={() => setAiSummaryModal({ open: false, orderId: null, orderLink: null, pdfProxyUrl: null, pdfStorageUrl: null })}
        orderId={aiSummaryModal.orderId}
        orderLink={aiSummaryModal.orderLink}
        pdfProxyUrl={aiSummaryModal.pdfProxyUrl}
        pdfStorageUrl={aiSummaryModal.pdfStorageUrl}
      />
      <SummarizeAllModal
        isOpen={summarizeAllModal.open}
        onClose={() => setSummarizeAllModal({ open: false, orders: [], courtType: null })}
        orders={summarizeAllModal.orders}
        courtType={summarizeAllModal.courtType}
      />
      </div>
    </div>
  );
}

export default CaseDetails;
